import React, { useState, useEffect } from 'react';
import MainLayout from '../../components/layout/MainLayout';
import HondaLoader from '../../components/common/HondaLoader';
import Swal from 'sweetalert2';
import FianceCard from '../../components/common/FianceCard';
import PersonaJuridicaForm from '../../components/common/PersonaJuridicaForm';
import ApoderadoForm from '../../components/common/ApoderadoForm';
import { useScoring, useScoringDataMapper, normalizeProvincia } from '../../hooks/useScoring';
import { uploadScoringDocument, setScoringStatus, parseValidationResult, resetCase } from '../../services/scoringService';
import { formatCurrencyInput, formatDateDDMMYYYY, formatDateYYYYMMDD, formatDateInput, validateDateDDMMYYYY, formatCuit } from '../../utils/inputValidation';
import {
    TIPO_PERSONA,
    GENERO,
    ESTADO_CIVIL,
    TIPO_INGRESO,
    CATEGORIA_FISCAL,
    getEstadoCivilTexto,
    getGeneroTexto,
    getTipoIngresoTexto,
    getCategoriaFiscalTexto,
    getTipoPersonaTexto,
    extractIdFromText
} from '../../constants/scoringConstants';
const AnalisisCrediticio = ({ user, onLogout, onNavigate, grupo, orden, estado }) => {
    // Hooks de scoring
    const { loading: scoringLoading, loadScoringInfo, validateTitular, validateWarrantor, saveScoringData, saveIncompleteScoringData, saveWarrantorData } = useScoring();
    const { mapTitularData } = useScoringDataMapper();
    // Forzar concesionario a 01050_MAIPU en todos lados
    const CONCESIONARIO_FORZADO = '01050_MAIPU';
    const [formData, setFormData] = useState({
        grupo: '',
        orden: '',
        tipoPersona: '1 - Persona Física',
        titular: '',
        cuit: '',
        fechaNacimiento: '',
        modelo: '',
        fechaAdjudicacion: '',
        saldoPagar: '',
        mesaPlan: '',
        fechaAceptacion: '',
        cuotasVencer: '',
        valorMovil: '',
        status: '',
        concesionario: CONCESIONARIO_FORZADO,
        tipoAdjudicacion: '',
        valorCuota: '',
        porcentajeDeuda: '',
        // Campos Persona Física
        estadoCivil: '',
        sexo: '',
        ingresosNetos: '',
        nacionalidad: '',
        tipoIngreso: '',
        pep: '',
        antiguedadLaboral: '',
        categoriaFiscal: '',
        numeroNupcias: '0',
        // Dirección (compartida entre Persona Física y Persona Jurídica)
        direccion: '',
        numero: '',
        pisoDpto: '',
        codigoPostal: '',
        localidad: '',
        provincia: '',
        telefono: '',
        email: '',
        // Campos Persona Jurídica - Empresa
        nombreEmpresa: '',
        cuitEmpresa: '',
        fechaConstitucion: '',
        tipoEmpresa: '',
        objetoEmpresa: '',
        ingresosEmpresa: '',
        pepEmpresa: '',
        telefonoEmpresa: '',
        emailEmpresa: '',
        // Campos Persona Jurídica - Apoderado
        apodNombre: '',
        apodDni: '',
        apodFechaNacimiento: '',
        apodEstadoCivil: '',
        apodSexo: '',
        apodNacionalidad: '',
        apodDireccion: '',
        apodNumero: '',
        apodPiso: '',
        apodCodigoPostal: '',
        apodLocalidad: '',
        apodProvincia: '',
        apodPep: '',
        // Cónyuge del Apoderado
        apodFianceCompleteName: '',
        apodFianceDocumentNumber: '',
        apodFianceBirthDate: '',
        apodFianceGender: '',
        apodFianceNationality: '',
    });
    const [garantes, setGarantes] = useState([]);
    const [garanteActual, setGaranteActual] = useState({
        nombreApellido: '',
        dni: '',
        fechaNacimiento: '',
        estadoCivil: '',
        sexo: '',
        calle: '',
        numero: '',
        altura: '',
        codigoPostal: '',
        localidad: '',
        provincia: '',
        telefono: '',
        email: '',
        tipoIngreso: '',
        ingresosNetos: '',
        antiguedadLaboral: '',
        nacionalidad: '',
        condicionFiscal: '',
        pep: '',
        // Datos del cónyuge del garante (estructura flat para consistencia)
        fianceCompleteName: '',
        fianceDocumentNumber: '',
        fianceBirthDate: '',
        fianceGender: '',
        fianceNationality: '',
        fiancePEP: ''
    });
    // Computed: detectar tipo de persona
    const isPersonaJuridica = formData.tipoPersona === '2 - Persona Jurídica' || formData.tipoPersona === '2';

    const [loading, setLoading] = useState(false);
    const [loadingGarante, setLoadingGarante] = useState(false);
    const [showResultados, setShowResultados] = useState(false);
    const [resultados, setResultados] = useState(null);
    const [garanteValidado, setGaranteValidado] = useState(false);
    const [resultadosGarante, setResultadosGarante] = useState(null);
    const [casoGuardado, setCasoGuardado] = useState(false);
    const [mostrarFormularioGarante, setMostrarFormularioGarante] = useState(false);
    const [garanteEnEdicion, setGaranteEnEdicion] = useState(-1); // -1 = nuevo garante, >= 0 = índice del garante en edición
    const [garantesRequeridos, setGarantesRequeridos] = useState(0); // Número de garantes que requiere el sistema (actualizado por validación)
    const [garantesExpandidos, setGarantesExpandidos] = useState({}); // Estado para controlar qué garantes están expandidos
    const [mostrarDocumentos, setMostrarDocumentos] = useState(false); // Controla si se muestra la sección de carga de documentos
    const [documentosTitular, setDocumentosTitular] = useState({
        dni: null,
        recibos: null,
        inscripcionMonotributo: null,
        pagosMonotributo: null,
        pagosGanancias: null,
        formulario711: null,
        cuit: null,
        formulario713: null,
        pagosIVA: null,
        dniapod: null,
        estatuto: null
    });
    const [documentosGarantes, setDocumentosGarantes] = useState([]);
    const [titularValidationResult, setTitularValidationResult] = useState(null); // Resultado de la validación del titular
    const [dataLoaded, setDataLoaded] = useState(false); // Para evitar llamadas múltiples
    const [idScoring, setIdScoring] = useState(null); // ID del scoring guardado
    const [fromCasos, setFromCasos] = useState(false); // Indica si viene de la pantalla de casos
    const [caseType, setCaseType] = useState('DISPONIBLE'); // Tipo de caso: DISPONIBLE o STORED

    const[nosisPEP, setNosisPEP] = useState(null); // Estado para almacenar el resultado de la validación PEP de Nosis
    const[nosisLAFT, setNosisLAFT] = useState(null); // Estado para almacenar el resultado de la validación LAFT de Nosis


    // useEffect para cargar datos al montar el componente
    useEffect(() => {
        const loadData = async () => {
            // Leer datos de localStorage primero
            const savedData = localStorage.getItem('scoringData');
            const currentSession = localStorage.getItem('currentScoringSession');
            let loadGrupo = grupo || '';
            let loadOrden = orden || '';
            let loadEstado = estado || null;
            let additionalData = {};
            // Priorizar datos de la navegación, luego de scoringData, luego de currentSession
            if (savedData) {
                try {
                    const parsed = JSON.parse(savedData);
                    loadGrupo = parsed.grupo || loadGrupo;
                    loadOrden = parsed.orden || loadOrden;
                    loadEstado = parsed.estado || loadEstado;
                    // Detectar si viene de la pantalla de casos
                    if (parsed.fromCasos === true) {
                        setFromCasos(true);
                        setCasoGuardado(true); // Marcar automáticamente como guardado
                        // Si viene con ID, guardarlo
                        if (parsed.idScoring) {
                            setIdScoring(parsed.idScoring);
                        }
                    }
                    // Guardar datos adicionales para usar como respaldo
                    additionalData = {
                        titular: parsed.titular,
                        cuil: parsed.cuil,
                        aceptacion: parsed.aceptacion,
                        concesionario: parsed.concesionario,
                        porcentajeDeuda: parsed.porcentajeDeuda,
                        aging: parsed.aging,
                        fromAvailableList: parsed.fromAvailableList
                    };
                } catch (error) {
                    console.error('Error parseando scoringData de localStorage:', error);
                }
            } else if (currentSession && !loadGrupo && !loadOrden) {
                // Si no hay datos de navegación ni scoringData, usar currentSession
                try {
                    const session = JSON.parse(currentSession);
                    loadGrupo = session.grupo;
                    loadOrden = session.orden;
                    loadEstado = session.estado;
                } catch (error) {
                    console.error('Error parseando currentScoringSession:', error);
                }
            }
            if (!loadGrupo || !loadOrden) {
                return;
            }
            if (dataLoaded) {
                return;
            }
            // Guardar sesión actual para permitir recargas
            localStorage.setItem('currentScoringSession', JSON.stringify({
                grupo: loadGrupo,
                orden: loadOrden,
                estado: loadEstado
            }));
            try {
                // Actualizar grupo y orden en el formulario PRIMERO, antes de cargar del API
                setFormData(prev => ({
                    ...prev,
                    grupo: loadGrupo,
                    orden: loadOrden
                }));
                // 🎯 Determinar tipo de caso y llamar al endpoint apropiado
                let determinedCaseType = 'DISPONIBLE';
                // Verificar si viene de casos incompletos/rechazados
                if (additionalData.fromAvailableList === true) {
                    determinedCaseType = 'DISPONIBLE';
                } else if (fromCasos || loadEstado === 'incompleto' || loadEstado === 'rechazado') {
                    // Tanto incompletos como rechazados usan GetInfoStored
                    determinedCaseType = 'STORED';
                }
                // Guardar el tipo de caso en el estado
                setCaseType(determinedCaseType);
                const data = await loadScoringInfo(loadGrupo, loadOrden, determinedCaseType);
                if (data && data.titularData && data.planData) {
                    // Tenemos datos válidos del API
                    let titularData = data.titularData;
                    let planData = data.planData;
                    // Mapear los datos al formulario
                    const mappedData = mapTitularData(titularData, planData);
                    setFormData(prev => ({
                        ...prev,
                        grupo: loadGrupo,
                        orden: loadOrden,
                        fianceCompleteName: titularData.fianceCompleteName || '',
                        fianceDocumentNumber: titularData.fianceDocumentNumber || '',
                        fianceBirthDate: titularData.fianceBirthDate || '',
                        fianceGender: titularData.fianceGender || '',
                        fianceNationality: titularData.fianceNationality || '',
                        fiancePEP: titularData.fiancePEP || '',
                        ...mappedData,
                        // Forzar concesionario a 01050_MAIPU
                        concesionario: CONCESIONARIO_FORZADO
                    }));

                    // Cargar apodData para Persona Jurídica
                    if (data.apodData) {
                        const apod = data.apodData;
                        setFormData(prev => ({
                            ...prev,
                            apodNombre: apod.apodPersonCompleteName || '',
                            apodDni: apod.apodPersonDocumentNumber || '',
                            apodFechaNacimiento: formatDateDDMMYYYY(apod.apodPersonBirthDate || ''),
                            apodEstadoCivil: extractIdFromText(apod.apodPersonMarriageStatus || ''),
                            apodSexo: extractIdFromText(apod.apodPersonGender || ''),
                            apodNacionalidad: apod.apodPersonNationality || '',
                            apodDireccion: apod.apodAddress || '',
                            apodNumero: apod.apodAddressNumber || '',
                            apodPiso: apod.apodAddressFloor || '',
                            apodCodigoPostal: apod.apodAddressPostalCode || '',
                            apodLocalidad: apod.apodAddressLocale || '',
                            apodProvincia: normalizeProvincia(apod.apodAddressProvince || ''),
                            apodPep: apod.apodPep || '',
                            apodFianceCompleteName: apod.apodFianceCompleteName || '',
                            apodFianceDocumentNumber: apod.apodFianceDocumentNumber || '',
                            apodFianceBirthDate: apod.apodFianceBirthDate || '',
                            apodFianceGender: apod.apodFianceGender || '',
                            apodFianceNationality: apod.apodFianceNationality || '',
                            apodFiancePEP: apod.apodFiancePEP || '',
                        }));
                    }

                    // 🎯 Inicializar garantes desde backend si existen
                    if (Array.isArray(data.warrantors) && data.warrantors.length > 0) {
                        const garantesMapeados = data.warrantors.map(warrantor => ({
                            nombreApellido: warrantor.warrantorFullName || '',
                            dni: warrantor.warrantorDNI || '',
                            fechaNacimiento: warrantor.warrantorBirthDate || '',
                            estadoCivil: warrantor.warrantorMarriageStatus || '',
                            sexo: warrantor.warrantorGender || '',
                            calle: warrantor.warrantorAddress || '',
                            numero: warrantor.warrantorAddressNumber || '',
                            altura: warrantor.warrantorAddressFloor || '',
                            codigoPostal: warrantor.warrantorAddressPostalCode || '',
                            localidad: warrantor.warrantorAddressLocale || '',
                            provincia: warrantor.warrantorAddressProvince || '',
                            telefono: warrantor.warrantorPhone || '',
                            email: warrantor.warrantorEmail || '',
                            tipoIngreso: warrantor.warrantorIncomeType || '',
                            ingresosNetos: warrantor.warrantorIncome || '',
                            antiguedadLaboral: warrantor.warrantorLaborTime || '',
                            nacionalidad: warrantor.warrantorNationality || '',
                            condicionFiscal: warrantor.warrantorTaxCategory || '',
                            pep: warrantor.warrantorPEP || '',


                            // Datos del cónyuge del garante (estructura flat para consistencia)
                            fianceCompleteName: warrantor.warrantorFianceCompleteName || '',
                            fianceDocumentNumber: warrantor.warrantorFianceDocumentNumber || '',
                            fianceBirthDate: warrantor.warrantorFianceBirthDate || '',
                            fianceGender: warrantor.warrantorFianceGender || '',
                            fianceNationality: warrantor.warrantorFianceNationality || '',
                            fiancePEP: warrantor.warrantorFiancePEP || '',
                            validado: true,
                            resultados: null
                        }));
                        setGarantes(garantesMapeados);
                    }

                    // 🎯 Si es un caso STORED (incompleto/rechazado) y tiene validationResult, mostrar automáticamente los resultados
                    if (determinedCaseType === 'STORED' && titularData.validationResult) {
                        // Parsear el resultado de validación
                        const parsedResult = parseValidationResult(titularData.validationResult);
                        // Setear el resultado de validación del titular
                        setTitularValidationResult({
                            result: titularData.validationResult,
                            parsed: parsedResult
                        });
                        // Actualizar el número de garantes requeridos
                        setGarantesRequeridos(parsedResult.garantesRequeridos);
                        // Mostrar resultados simulados (estos pueden venir del backend en el futuro)
                        setResultados({
                            scoreVeraz: 856,
                            resultadoVeraz: parsedResult.esApto ? 'APTO' : 'NO APTO',
                            porcentajeDeuda: mappedData.porcentajeDeuda || '0,00%',
                            rci: '33.73%',
                            rciStatus: parsedResult.esApto ? 'APTO' : 'NO APTO',
                            validacionFinanciera: titularData.validationResult,
                            garantesRequeridos: parsedResult.garantesRequeridos,
                            requiereConstancia: parsedResult.requiereConstancia,
                            detalles: {
                                status12MBCRA: 'Normal',
                                statusActualBCRA: 'Normal',
                                chequesRechazados: 0,
                                juicios12Meses: 0,
                                concursosQuiebras60Meses: 0
                            }
                        });
                        // Mostrar la sección de resultados
                        setShowResultados(true);
                    }
                    // Limpiar localStorage solo después de cargar datos exitosamente
                    localStorage.removeItem('scoringData');
                    setDataLoaded(true);
                } else {
                    // No hay datos del API, usar datos de localStorage
                    if (additionalData.titular) {
                        setFormData(prev => ({
                            ...prev,
                            grupo: loadGrupo,
                            orden: loadOrden,
                            titular: additionalData.titular || '',
                            cuit: additionalData.cuil || '',
                            fechaAceptacion: additionalData.aceptacion || '',
                            concesionario: CONCESIONARIO_FORZADO,
                            porcentajeDeuda: additionalData.porcentajeDeuda || ''
                        }));
                        // NO limpiar localStorage aquí - los datos pueden ser útiles
                    }
                    setDataLoaded(true);
                }
            } catch (error) {
                // Logging condicional: solo error si no es un caso esperado
                if (error.message && error.message.includes('no encontrado')) {
                } else {
                    console.error('Error cargando datos del scoring:', error);
                }
                // En caso de error, usar datos de respaldo de localStorage
                if (additionalData.titular) {
                    setFormData(prev => ({
                        ...prev,
                        grupo: loadGrupo,
                        orden: loadOrden,
                        titular: additionalData.titular || '',
                        cuit: additionalData.cuil || '',
                        fechaAceptacion: additionalData.aceptacion || '',
                        concesionario: CONCESIONARIO_FORZADO,
                        porcentajeDeuda: additionalData.porcentajeDeuda || ''
                    }));
                    // Nota: Alerta deshabilitada - datos cargados correctamente desde localStorage
                    // Solo se muestra si realmente no se pueden cargar datos
                } else {
                    // Solo mostrar alerta si NO hay datos de respaldo
                    Swal.fire({
                        icon: 'warning',
                        title: 'Error al cargar datos',
                        text: 'No se pudieron cargar los datos del scoring. Por favor, ingrese la información manualmente.',
                        confirmButtonColor: '#ef4444',
                    });
                }
                // Marcar como cargado para no seguir intentando
                setDataLoaded(true);
            }
        };
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const handleChange = (e) => {
        const { name, value } = e.target;

        // Si es el campo de ingresos netos, aplicar formato con separadores de miles
        if (name === 'ingresosNetos') {
            const formattedValue = formatCurrencyInput(value);
            setFormData(prev => ({
                ...prev,
                [name]: formattedValue
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };
    const handleGaranteChange = (e) => {
        const { name, value } = e.target;

        // Si es el campo de ingresos netos, aplicar formato con separadores de miles
        if (name === 'ingresosNetos') {
            const formattedValue = formatCurrencyInput(value);
            setGaranteActual(prev => ({
                ...prev,
                [name]: formattedValue
            }));
        } else {
            setGaranteActual(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };
    const handleValidarEmail = () => {
        if (formData.email) {
            alert(`Validando email: ${formData.email}`);
        } else {
            alert('Por favor ingrese un email primero');
        }
    };
    const handleValidarEmailGarante = () => {
        if (garanteActual.email) {
            alert(`Validando email del garante: ${garanteActual.email}`);
        } else {
            alert('Por favor ingrese un email para el garante');
        }
    };
    const esMayorDeEdad = (fechaNacimiento) => {
        if (!fechaNacimiento) return false;

        const [dia, mes, anio] = fechaNacimiento.split('/');

        const nacimiento = new Date(
            parseInt(anio),
            parseInt(mes) - 1,
            parseInt(dia)
        );

        const hoy = new Date();

        let edad = hoy.getFullYear() - nacimiento.getFullYear();

        const mesDiff = hoy.getMonth() - nacimiento.getMonth();

        if (
            mesDiff < 0 ||
            (mesDiff === 0 && hoy.getDate() < nacimiento.getDate())
        ) {
            edad--;
        }

        return edad >= 18;
    };
    const esMayorDe65 = (fechaNacimiento) => {
        if (!fechaNacimiento) return false;

        const [dia, mes, anio] = fechaNacimiento.split('/');

        const nacimiento = new Date(
            parseInt(anio, 10),
            parseInt(mes, 10) - 1,
            parseInt(dia, 10)
        );

        const hoy = new Date();

        let edad = hoy.getFullYear() - nacimiento.getFullYear();

        const mesDiff = hoy.getMonth() - nacimiento.getMonth();

        if (
            mesDiff < 0 ||
            (mesDiff === 0 && hoy.getDate() < nacimiento.getDate())
        ) {
            edad--;
        }

        return edad > 65;
    };
    const validateRequiredFields = () => {
        if (isPersonaJuridica) {
            const empresaFields = [
                formData.tipoEmpresa,
                formData.objetoEmpresa,
                formData.ingresosEmpresa,
                formData.pepEmpresa,
                formData.direccion,
                formData.numero,
                formData.codigoPostal,
                formData.localidad,
                formData.provincia,
                formData.telefonoEmpresa,
                formData.emailEmpresa,
            ];
            if (!empresaFields.every(f => f && f.toString().trim() !== '')) return false;
            const apodFields = [
                formData.apodNombre,
                formData.apodDni,
                formData.apodFechaNacimiento,
                formData.apodEstadoCivil,
                formData.apodSexo,
                formData.apodNacionalidad,
                formData.apodPep,
                formData.apodDireccion,
                formData.apodNumero,
                formData.apodCodigoPostal,
                formData.apodLocalidad,
                formData.apodProvincia,
            ];
            if (!apodFields.every(f => f && f.toString().trim() !== '')) return false;
            if (formData.apodEstadoCivil === '2') {
                const apodFianceFields = [
                    formData.apodFianceCompleteName,
                    formData.apodFianceDocumentNumber,
                    formData.apodFianceBirthDate,
                    formData.apodFianceGender,
                    formData.apodFianceNationality,
                    formData.apodFiancePEP,
                ];
                if (!apodFianceFields.every(f => f && f.toString().trim() !== '')) return false;
            }
            return true;
        } else {
            const fields = [
                formData.estadoCivil,
                formData.sexo,
                formData.ingresosNetos,
                formData.tipoIngreso,
                formData.nacionalidad,
                formData.pep,
                formData.antiguedadLaboral,
                formData.categoriaFiscal,
                formData.direccion,
                formData.numero,
                formData.codigoPostal,
                formData.localidad,
                formData.provincia,
                formData.telefono,
                formData.email,
            ];
            return fields.every(f => f && f.toString().trim() !== '');
        }
    };

    const validateGaranteFields = () => {
        const fields = [
            garanteActual.nombreApellido,
            garanteActual.dni,
            garanteActual.estadoCivil,
            garanteActual.fechaNacimiento,
            garanteActual.sexo,
            garanteActual.calle,
            garanteActual.numero,
            garanteActual.codigoPostal,
            garanteActual.localidad,
            garanteActual.provincia,
            garanteActual.telefono,
            garanteActual.email,
            garanteActual.tipoIngreso,
            garanteActual.ingresosNetos,
            garanteActual.antiguedadLaboral,
            garanteActual.nacionalidad,
            garanteActual.condicionFiscal,
        ];
        if (!fields.every(f => f && f.toString().trim() !== '')) return false;
        if (garanteActual.estadoCivil === '2') {
            const fianceFields = [
                garanteActual.fianceCompleteName,
                garanteActual.fianceDocumentNumber,
                garanteActual.fianceBirthDate,
                garanteActual.fianceGender,
                garanteActual.fianceNationality,
                garanteActual.fiancePEP,
            ];
            if (!fianceFields.every(f => f && f.toString().trim() !== '')) return false;
        }
        return true;
    };

    const handleVerificarCrediticio = async () => {
        if (!validateRequiredFields()) {
            Swal.fire({
                icon: 'warning',
                title: 'Datos incompletos',
                text: 'Debe completar todos los datos obligatorios para continuar.',
                confirmButtonColor: '#ef4444',
            });
            return;
        }
        const docNumber = isPersonaJuridica ? (formData.cuitEmpresa || formData.cuit) : formData.cuit;
        const ingresosField = isPersonaJuridica ? formData.ingresosEmpresa : formData.ingresosNetos;

        if (!docNumber) {
            Swal.fire({
                icon: 'warning',
                title: isPersonaJuridica ? 'CUIT Requerido' : 'DNI Requerido',
                text: isPersonaJuridica ? 'Por favor ingrese el CUIT de la empresa' : 'Por favor ingrese el DNI del titular',
                confirmButtonColor: '#ef4444',
            });
            return;
        }
        if (!ingresosField) {
            Swal.fire({
                icon: 'warning',
                title: 'Ingresos Requeridos',
                text: 'Por favor ingrese los ingresos del titular',
                confirmButtonColor: '#ef4444',
            });
            return;
        }
        try {
            setLoading(true);
            const quotaValue = parseFloat(
                formData.valorCuota?.toString().replace(/\./g, '').replace(/,/g, '.')
            ) || 0;

            const income = parseFloat(
                ingresosField?.toString().replace(/\./g, '').replace(/,/g, '.')
            ) || 0;

            const debtPercent = parseFloat(
                formData.porcentajeDeuda?.toString()
                    .replace(/%/g, '')
                    .replace(',', '.')
            ) || 0;

            const result = await validateTitular(docNumber, quotaValue, income, debtPercent);
            setTitularValidationResult(result);

            // Crear copia del resultado sin la propiedad "parsed"
            const { parsed, ...resultWithoutParsed } = result;


            /*
            //ONLY FOR DEBUG PURPOSES
            await Swal.fire({
                title: 'Respuesta de Validación',
                html: `
                    <div class="text-center">
                        <div class="mb-3 p-3 bg-yellow-100 border border-yellow-300 rounded-lg text-yellow-800 text-sm font-medium">
                            ⚠️ Información visible únicamente para pruebas. Este mensaje será removido en producción.
                        </div>

                        <div class="bg-gray-50 p-4 rounded-lg text-sm">
                            <pre class="whitespace-pre-wrap text-xs">${JSON.stringify(resultWithoutParsed, null, 2)}</pre>
                        </div>
                    </div>
                `,
                icon: 'info',
                confirmButtonText: 'Continuar',
                confirmButtonColor: '#ef4444',
            });

            */

            // Actualizar el número de garantes requeridos
            if (result.parsed) {
                setGarantesRequeridos(result.parsed.garantesRequeridos);
            }
            // Mostrar resultados simulados de Veraz (esto puede venir de otro endpoint)
            setResultados({
                scoreVeraz: 856,
                resultadoVeraz: result.parsed.esApto ? 'APTO' : 'NO APTO',
                porcentajeDeuda: formData.porcentajeDeuda || '0,00%',
                rci: '33.73%',
                rciStatus: result.parsed.esApto ? 'APTO' : 'NO APTO',
                validacionFinanciera: result.result,
                garantesRequeridos: result.parsed.garantesRequeridos,
                requiereConstancia: result.parsed.requiereConstancia,
                detalles: {
                    status12MBCRA: 'Normal',
                    statusActualBCRA: 'Normal',
                    chequesRechazados: 0,
                    juicios12Meses: 0,
                    concursosQuiebras60Meses: 0,
                    ...(result.details || {})
                }
            });
            setShowResultados(true);
        } catch (error) {
            console.error('Error en validación:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error en Validación',
                text: error.message || 'No se pudo validar el titular',
                confirmButtonColor: '#ef4444',
            });
        } finally {
            setLoading(false);
        }
    };
    const handleValidarGaranteVeraz = async () => {
        if (!validateGaranteFields()) {
            Swal.fire({
                icon: 'warning',
                title: 'Datos incompletos',
                text: 'Debe completar todos los datos obligatorios para continuar.',
                confirmButtonColor: '#ef4444',
            });
            return;
        }
        if (!garanteActual.fechaNacimiento) {
            Swal.fire({
                icon: 'warning',
                title: 'Fecha de nacimiento requerida',
                text: 'Por favor ingrese la fecha de nacimiento del garante',
                confirmButtonColor: '#ef4444',
            });
            return;
        }

        if (!esMayorDeEdad(garanteActual.fechaNacimiento)) {
            Swal.fire({
                icon: 'error',
                title: 'Garante menor de edad',
                text: 'El garante debe ser mayor de 18 años para continuar.',
                confirmButtonColor: '#ef4444',
            });
            return;
        }
        if (esMayorDe65(garanteActual.fechaNacimiento)) {
            Swal.fire({
                icon: 'error',
                title: 'Garante mayor de edad',
                text: 'El garante debe ser menor de 65 años para continuar.',
                confirmButtonColor: '#ef4444',
            });
            return;
        }

        if (!garanteActual.dni) {
            Swal.fire({
                icon: 'warning',
                title: 'DNI requerido',
                text: 'Por favor ingrese el DNI del garante',
                confirmButtonColor: '#ef4444',
            });
            return;
        }
        try {
            setLoadingGarante(true);
            const quotaValue = parseFloat(
                formData.valorCuota?.toString().replace(/\./g, '').replace(/,/g, '.')
            ) || 0;

            const income = parseFloat(
                garanteActual.ingresosNetos?.toString().replace(/\./g, '').replace(/,/g, '.')
            ) || 0;

            const debtPercent = parseFloat(
                formData.porcentajeDeuda?.toString()
                    .replace(/%/g, '')
                    .replace(',', '.')
            ) || 0;
            const result = await validateWarrantor(garanteActual.dni, quotaValue, income, debtPercent);

            // Crear copia del resultado sin la propiedad "parsed"
            const { parsed, ...resultWithoutParsed } = result;

            /*
            //ONLY FOR DEBUG PURPOSES
            await Swal.fire({
                title: 'Respuesta de Validación - Garante',
                html: `
                    <div class="text-center">
                        <div class="mb-3 p-3 bg-yellow-100 border border-yellow-300 rounded-lg text-yellow-800 text-sm font-medium">
                            ⚠️ Información visible únicamente para pruebas. Este mensaje será removido en producción.
                        </div>

                        <div class="bg-gray-50 p-4 rounded-lg text-sm">
                            <pre class="whitespace-pre-wrap text-xs">${JSON.stringify(resultWithoutParsed, null, 2)}</pre>
                        </div>
                    </div>
                `,
                icon: 'info',
                confirmButtonText: 'Continuar',
                confirmButtonColor: '#ef4444',
            });
            */

            // Actualizar resultados del garante
            setResultadosGarante({
                scoreVeraz: 720,
                resultadoVeraz: result.result === 'APROBADO' ? 'APTO' : 'NO APTO',
                porcentajeDeuda: '35.20%',
                rci: '18.50%',
                rciStatus: result.result === 'APROBADO' ? 'APTO' : 'NO APTO',
                validacionFinanciera: result.result,
                detalles: {
                    status12MBCRA: 'Normal',
                    statusActualBCRA: 'Normal',
                    chequesRechazados: 0,
                    juicios12Meses: 0,
                    concursosQuiebras60Meses: 0,
                    ...(result.details || {})
                }
            });
            // Solo marcar como validado si fue aprobado
            setGaranteValidado(result.result === 'APROBADO');
            if (result.result === 'APROBADO') {
                Swal.fire({
                    icon: 'success',
                    title: 'Garante Validado',
                    text: 'El garante ha sido validado correctamente',
                    confirmButtonColor: '#ef4444',
                    timer: 2000,
                });
            } else {
                Swal.fire({
                    icon: 'warning',
                    title: 'Garante No Aprobado',
                    text: `Resultado: ${result.result}`,
                    confirmButtonColor: '#ef4444',
                });
            }
        } catch (error) {
            console.error('Error en validación de garante:', error);
            setGaranteValidado(false);
            Swal.fire({
                icon: 'error',
                title: 'Error en Validación',
                text: error.message || 'No se pudo validar el garante',
                confirmButtonColor: '#ef4444',
            });
        } finally {
            setLoadingGarante(false);
        }
    };
    const handleAgregarGarante = () => {
        if (!garanteValidado) {
            alert('Debe validar al garante en Veraz primero');
            return;
        }
        let indexGarante;
        if (garanteEnEdicion >= 0) {
            // Editando un garante existente
            const nuevosGarantes = [...garantes];
            nuevosGarantes[garanteEnEdicion] = { ...garanteActual, validado: true, resultados: resultadosGarante };
            setGarantes(nuevosGarantes);
            indexGarante = garanteEnEdicion;
        } else {
            // Agregando nuevo garante
            const nuevoGarante = { ...garanteActual, validado: true, resultados: resultadosGarante };
            setGarantes([...garantes, nuevoGarante]);
            indexGarante = garantes.length;
        }
        // Expandir el garante agregado/editado por defecto
        setGarantesExpandidos(prev => ({ ...prev, [indexGarante]: true }));
        // Resetear el formulario
        setGaranteActual({
            nombreApellido: '',
            dni: '',
            fechaNacimiento: '',
            estadoCivil: '',
            sexo: '',
            calle: '',
            numero: '',
            altura: '',
            codigoPostal: '',
            localidad: '',
            provincia: '',
            telefono: '',
            email: '',
            tipoIngreso: '',
            ingresosNetos: '',
            antiguedadLaboral: '',
            nacionalidad: '',
            condicionFiscal: '',
            pep: '',
            fianceCompleteName: '',
            fianceDocumentNumber: '',
            fianceBirthDate: '',
            fianceGender: '',
            fianceNationality: '',
            fiancePEP: ''
        });
        setGaranteValidado(false);
        setResultadosGarante(null);
        setGaranteEnEdicion(-1);
        // Verificar si se necesitan más garantes
        const totalGarantes = garanteEnEdicion >= 0 ? garantes.length : garantes.length + 1;
        if (totalGarantes < garantesRequeridos) {
            // Mostrar el siguiente formulario automáticamente
            setMostrarFormularioGarante(true);
        } else {
            // Ocultar el formulario si ya se alcanzó la cantidad requerida
            setMostrarFormularioGarante(false);
        }
    };
    const handleVolver = () => {
        setShowResultados(false);
        setResultados(null);
        setGarantes([]);
        setGaranteValidado(false);
        setResultadosGarante(null);
        setCasoGuardado(false);
        setMostrarFormularioGarante(false);
    };
    const handleGuardarCaso = async () => {
        if (!validateRequiredFields()) {
            Swal.fire({
                icon: 'warning',
                title: 'Datos incompletos',
                text: 'Debe completar todos los datos obligatorios para continuar.',
                confirmButtonColor: '#ef4444',
            });
            return;
        }
        try {
            // Formatear grupo y orden
            const grupoFormateado = String(formData.grupo).trim().padStart(6, '0');
            const ordenFormateada = String(formData.orden).trim().padStart(4, '0');

            // Fechas del plan (compartidas entre PF y PJ)
            const fechaAdjudicacionSQL = formData.fechaAdjudicacion ? formatDateYYYYMMDD(formData.fechaAdjudicacion) : '';
            const fechaAceptacionSQL = formData.fechaAceptacion ? formatDateYYYYMMDD(formData.fechaAceptacion) : '';

            const planData = {
                productName: formData.modelo || '',
                productValue: parseFloat(formData.valorMovil?.toString().replace(/\./g, '').replace(/,/g, '.')) || 0,
                monthsPlan: parseInt(formData.mesaPlan) || 0,
                debtValue: parseFloat(formData.saldoPagar?.toString().replace(/\./g, '').replace(/,/g, '.')) || 0,
                debtPercent: parseFloat(formData.porcentajeDeuda?.toString().replace('%', '').replace(/,/g, '.')) || 0,
                futureValue: parseFloat(formData.valorCuota?.toString().replace(/\./g, '').replace(/,/g, '.')) || 0,
                quotasLeft: parseInt(formData.cuotasVencer) || 0,
                adjType: formData.tipoAdjudicacion || '',
                adjDate: fechaAdjudicacionSQL,
                acceptDate: fechaAceptacionSQL,
                sellerHouse: {
                    id: 1050,
                    ceName: CONCESIONARIO_FORZADO
                }
            };


            //Esta variable guarda los datos que se enviaron a guardar en incompleto
            let scoringData;

            if (isPersonaJuridica) {
                // ===== PAYLOAD PERSONA JURÍDICA =====
                const apodFechaNacimientoSQL = formData.apodFechaNacimiento ? formatDateYYYYMMDD(formData.apodFechaNacimiento) : '';

                scoringData = {
                    group: grupoFormateado,
                    order: ordenFormateada,
                    userID: user?.id || user?.userId || 0,
                    titularData: {
                        personNewconID: formData.cuitEmpresa || '',
                        personType: '2 - Persona Jurídica',
                        personBirthDate: null,
                        personMarriageStatus: null,
                        personGender: null,
                        personNationality: null,
                        personlaborTime: null,
                        fianceCompleteName: null,
                        fianceDocumentNumber: null,
                        fianceBirthDate: null,
                        fianceGender: null,
                        fianceNationality: null,
                        fiancePEP: null,
                        companyDate: formData.fechaConstitucion ? formatDateYYYYMMDD(formData.fechaConstitucion) : null,
                        companyType: formData.tipoEmpresa || '',
                        companyPurpose: formData.objetoEmpresa || '',
                        documentType: 'CUIT',
                        documentNumber: formData.cuitEmpresa || '',
                        completeName: formData.nombreEmpresa || formData.titular || '',
                        taxCategory: null,
                        address: formData.direccion || '',
                        addressNumber: formData.numero || '',
                        addressFloor: formData.pisoDpto || '',
                        addressPostalCode: formData.codigoPostal || '',
                        addressLocale: formData.localidad || '',
                        addressProvince: formData.provincia || '',
                        incomeType: null,
                        income: formData.ingresosEmpresa || '',
                        pep: formData.pepEmpresa || '',
                        phone: formData.telefonoEmpresa || '',
                        mail: formData.emailEmpresa || '',
                        validationResult: titularValidationResult?.result || '',
                        equifaxValues: titularValidationResult?.node.toUpperCase() + " | " + titularValidationResult?.scorePopulation + " | " + titularValidationResult?.scoreVeraz
                    },
                    apodData: {
                        apodPersonDocumentType: 'DNI',
                        apodPersonDocumentNumber: formData.apodDni || '',
                        apodPersonCompleteName: formData.apodNombre || '',
                        apodPersonBirthDate: apodFechaNacimientoSQL,
                        apodPersonMarriageStatus: formData.apodEstadoCivil ? getEstadoCivilTexto(formData.apodEstadoCivil) : '',
                        apodPersonGender: formData.apodSexo ? getGeneroTexto(formData.apodSexo) : '',
                        apodPersonNationality: formData.apodNacionalidad || '',
                        apodAddress: formData.apodDireccion || '',
                        apodAddressNumber: formData.apodNumero || '',
                        apodAddressFloor: formData.apodPiso || '',
                        apodAddressPostalCode: formData.apodCodigoPostal || '',
                        apodAddressLocale: formData.apodLocalidad || '',
                        apodAddressProvince: formData.apodProvincia || '',
                        apodPep: formData.apodPep || '',
                        apodFianceCompleteName: formData.apodFianceCompleteName || null,
                        apodFianceDocumentNumber: formData.apodFianceDocumentNumber || null,
                        apodFianceBirthDate: formData.apodFianceBirthDate || null,
                        apodFianceGender: formData.apodFianceGender || null,
                        apodFianceNationality: formData.apodFianceNationality || null,
                        apodFiancePEP: formData.apodFiancePEP || '',
                    },
                    planData
                };
            } else {
                // ===== PAYLOAD PERSONA FÍSICA (sin cambios) =====
                const fechaNacimientoSQL = formData.fechaNacimiento ? formatDateYYYYMMDD(formData.fechaNacimiento) : '';

                scoringData = {
                    group: grupoFormateado,
                    order: ordenFormateada,
                    userID: user?.id || user?.userId || 0,
                    titularData: {
                        personNewconID: formData.cuit || '',
                        personType: formData.tipoPersona ? getTipoPersonaTexto(formData.tipoPersona) : '1 - Persona Física',
                        personBirthDate: fechaNacimientoSQL,
                        personMarriageStatus: formData.estadoCivil ? getEstadoCivilTexto(formData.estadoCivil) : '1 - Soltero',
                        personGender: formData.sexo ? getGeneroTexto(formData.sexo) : '1 - Masculino',
                        personNationality: formData.nacionalidad || '',
                        personlaborTime: formData.antiguedadLaboral || '',
                        fianceCompleteName: formData.fianceCompleteName || '',
                        fianceDocumentNumber: formData.fianceDocumentNumber || '',
                        fianceBirthDate: formData.fianceBirthDate || '',
                        fianceGender: formData.fianceGender || '',
                        fianceNationality: formData.fianceNationality || '',
                        fiancePEP: formData.fiancePEP || '',
                        companyDate: null,
                        companyType: null,
                        companyPurpose: null,
                        documentType: '1 - DNI',
                        documentNumber: formData.cuit || '',
                        completeName: formData.titular || '',
                        taxCategory: formData.categoriaFiscal ? getCategoriaFiscalTexto(formData.categoriaFiscal) : '1 - Consumidor Final',
                        address: formData.direccion || '',
                        addressNumber: formData.numero || '',
                        addressFloor: formData.pisoDpto || '',
                        addressPostalCode: formData.codigoPostal || '',
                        addressLocale: formData.localidad || '',
                        addressProvince: formData.provincia || '',
                        incomeType: formData.tipoIngreso ? getTipoIngresoTexto(formData.tipoIngreso) : '1 - Empleado',
                        income: formData.ingresosNetos || '',
                        pep: formData.pep || '',
                        phone: formData.telefono || '',
                        mail: formData.email || '',
                        validationResult: titularValidationResult?.result || '',
                        nosisPEP: titularValidationResult?.nosisPEP || '',
                        nosisLAFT: titularValidationResult?.nosisLAFT || '',
                        equifaxValues: titularValidationResult?.node.toUpperCase() + " | " + titularValidationResult?.scorePopulation + " | " + titularValidationResult?.scoreVeraz
                    },
                    apodData: null,
                    planData
                };
            }
            // Llamar al nuevo endpoint StoreIncompleteScoring
            const response = await saveIncompleteScoringData(scoringData);
            // Guardar el ID del scoring para usarlo en la carga de documentos
            // El backend puede retornar el ID directamente como número o como objeto
            ////MEJORAR AQUI
            let scoringId = null;
            if (typeof response === 'number') {
                scoringId = response;
            } else if (response && response.id) {
                scoringId = response.id;
            } else if (response && response.idScoring) {
                scoringId = response.idScoring;
            }
            if (scoringId) {
                setIdScoring(scoringId);
            } else {
            }
            setCasoGuardado(true);

            //Almacenar pep y laft para decidir luego si va a revision o revision especial
            setNosisPEP(titularValidationResult?.nosisPEP || '');
            setNosisLAFT(titularValidationResult?.nosisLAFT || '');


            // Limpiar sesión guardada después de guardar exitosamente
            localStorage.removeItem('currentScoringSession');
        } catch (error) {
            console.error('Error al guardar caso:', error);
        }
    };
    const handleContinuar = () => {
        if (!casoGuardado) {
            Swal.fire({
                title: 'Atención',
                text: 'Debe guardar el caso antes de continuar',
                icon: 'warning',
                confirmButtonText: 'Aceptar',
                confirmButtonColor: '#ef4444'
            });
            return;
        }
        // Si no se requieren garantes (titular APTO o solo constancia), ir directo a documentos
        if (garantesRequeridos === 0) {
            setMostrarDocumentos(true);
            // Inicializar documentos de garantes vacío ya que no hay garantes
            setDocumentosGarantes([]);
        } else {
            // Si se requieren garantes, mostrar el formulario
            setMostrarFormularioGarante(true);
            setGaranteEnEdicion(-1); // Nuevo garante
        }
    };
    const handleEditarGarante = (index) => {
        // Cargar los datos del garante en el formulario
        setGaranteActual(garantes[index]);
        // Al editar, se debe volver a validar en Veraz
        setResultadosGarante(null);
        setGaranteValidado(false);
        setGaranteEnEdicion(index);
        setMostrarFormularioGarante(true);
        // Colapsar la información del garante al editar
        setGarantesExpandidos(prev => ({ ...prev, [index]: false }));
    };
    const handleAgregarNuevoGarante = () => {
        setGaranteEnEdicion(-1);
        setMostrarFormularioGarante(true);
    };
    const toggleGaranteExpandido = (index) => {
        setGarantesExpandidos(prev => ({
            ...prev,
            [index]: !prev[index]
        }));
    };
    const handleContinuarDocumentos = () => {
        setMostrarDocumentos(true);
        // Inicializar documentos de garantes
        const docsGarantes = garantes.map(() => ({
            dni: null,
            recibos: null,
            inscripcionMonotributo: null,
            pagosMonotributo: null,
            pagosGanancias: null,
            formulario711: null
        }));
        setDocumentosGarantes(docsGarantes);
    };
    const handleReiniciarScoring = async () => {
        try {
            // Validar que haya grupo y orden
            if (!formData.grupo || !formData.orden) {
                await Swal.fire({
                    icon: 'warning',
                    title: 'Datos Incompletos',
                    text: 'No se encontró información de grupo y orden para reiniciar el caso.',
                    confirmButtonColor: '#ef4444',
                });
                return;
            }

            // Confirmar antes de reiniciar
            const result = await Swal.fire({
                title: '¿Reiniciar Scoring?',
                text: 'Esto iniciará el proceso de scoring desde cero. ¿Desea continuar?',
                icon: 'question',
                showCancelButton: true,
                confirmButtonText: 'Sí, reiniciar',
                cancelButtonText: 'Cancelar',
                confirmButtonColor: '#ef4444',
                cancelButtonColor: '#6b7280',
            });

            if (result.isConfirmed) {
                // Mostrar loading
                Swal.fire({
                    title: 'Reiniciando Scoring',
                    text: 'Por favor espere...',
                    allowOutsideClick: false,
                    allowEscapeKey: false,
                    showConfirmButton: false,
                    didOpen: () => {
                        Swal.showLoading();
                    }
                });

                // Llamar al endpoint resetCase con los parámetros correctos
                await resetCase(formData.grupo, formData.orden, user.id);

                // Resetear el estado local
                handleVolver();
                setMostrarDocumentos(false);
                setCasoGuardado(false);
                setIdScoring(null);

                // Mostrar mensaje de éxito
                await Swal.fire({
                    icon: 'success',
                    title: 'Scoring Reiniciado',
                    text: 'El proceso de scoring ha sido reiniciado exitosamente',
                    confirmButtonColor: '#ef4444',
                    timer: 2000,
                });
            }
        } catch (error) {
            console.error('Error al reiniciar scoring:', error);
            await Swal.fire({
                icon: 'error',
                title: 'Error al Reiniciar',
                text: error.message || 'No se pudo reiniciar el scoring',
                confirmButtonColor: '#ef4444',
            });
        }
    };
    const handleFileChange = (tipo, archivo, garanteIndex = null) => {
        if (garanteIndex !== null) {
            // Actualizar documentos de garante
            setDocumentosGarantes(prev => {
                const newDocs = [...prev];
                newDocs[garanteIndex][tipo] = archivo;
                return newDocs;
            });
        } else {
            // Actualizar documentos del titular
            setDocumentosTitular(prev => ({
                ...prev,
                [tipo]: archivo
            }));
        }
    };
    const getDocumentosRequeridos = (tipoPersona, tipoIngreso) => {
        // Normalizar tipoIngreso: puede venir como ID ("1") o como texto completo ("1 - Empleado")
        // Esto ocurre cuando los datos se cargan de un caso rechazado/incompleto (warrantorIncomeType)
        const tipoIngresoNormalizado = extractIdFromText(tipoIngreso);

        // Persona Jurídica (acepta valor completo o numérico por compatibilidad)
        if (tipoPersona === '2 - Persona Jurídica' || tipoPersona === '2') {
            return {
                tipo: 'juridica',
                documentos: [
                    { key: 'cuit', label: 'CUIT', multiple: false },
                    { key: 'estatuto', label: 'Estatuto o contrato social', multiple: false },
                    { key: 'formulario713', label: 'Último balance / Formulario 713', multiple: false },
                    { key: 'pagosIVA', label: 'Últimos 3 Pagos de IVA', multiple: false }, 
                    { key: 'dniapod', label: 'DNI del apoderado + Poder correspondiente', multiple: false },
                ]
            };
        }
        console.log(titularValidationResult);
        // Persona Física según tipo de ingreso
        // 1 - Empleado
        if (tipoIngresoNormalizado === '1' || tipoIngresoNormalizado === 'relacionDependencia') {

            if(titularValidationResult?.parsed.requiereConstancia){

                return {
                    tipo: 'empleado',
                    documentos: [
                        { key: 'dni', label: 'DNI Titular', multiple: false },
                        { key: 'recibos', label: 'Últimos 3 Recibos de Sueldo', multiple: false }
                    ]
                };

            } else {

                return {
                    tipo: 'empleado',
                    documentos: [
                        { key: 'dni', label: 'DNI Titular', multiple: false }
                    ]
                };

            }
        }
        // 2 - Monotributo
        else if (tipoIngresoNormalizado === '2' || tipoIngresoNormalizado === 'monotributo') {

            if(titularValidationResult?.parsed.requiereConstancia){

                return {
                    tipo: 'monotributista',
                    documentos: [
                        { key: 'dni', label: 'DNI Titular', multiple: false },
                        { key: 'inscripcionMonotributo', label: 'Inscripción Monotributo', multiple: false },
                        { key: 'pagosMonotributo', label: 'Últimos 3 Pagos del Monotributo', multiple: false }
                    ]
                };

            }else {

                return {
                    tipo: 'monotributista',
                    documentos: [
                        { key: 'dni', label: 'DNI Titular', multiple: false }
                    ]
                };

            }
        }
        // 3 - Responsable Inscripto (Autónomo)
        else if (tipoIngresoNormalizado === '3' || tipoIngresoNormalizado === 'autonomo') {

            if(titularValidationResult?.parsed.requiereConstancia){
                
                return {
                    tipo: 'autonomo',
                    documentos: [
                        { key: 'dni', label: 'DNI Titular', multiple: false },
                        { key: 'formulario711', label: 'Formulario 711', multiple: false },
                        { key: 'pagosGanancias', label: 'Últimos 3 Pagos DDJJ Ganancias', multiple: false }
                    ]
                };

            } else {

                    return {
                    tipo: 'autonomo',
                    documentos: [
                        { key: 'dni', label: 'DNI Titular', multiple: false }
                    ]
                };

            }
        }
        // Por defecto, solo DNI
        return {
            tipo: 'default',
            documentos: [
                { key: 'dni', label: 'DNI Titular', multiple: false }
            ]
        };
    };
    const handleFinalizarCargaDocumentos = async () => {
        try {
            // Validar que exista el ID del scoring
            if (!idScoring) {
                await Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'No se encontró el ID del scoring. Debe guardar el caso antes de cargar documentos.',
                    confirmButtonColor: '#ef4444',
                });
                return;
            }

            // ========== VALIDACIÓN DE DOCUMENTOS OBLIGATORIOS ==========
            const documentosFaltantes = [];

            // 1. Validar documentos del titular
            const docsRequeridosTitular = getDocumentosRequeridos(formData.tipoPersona, formData.tipoIngreso);
            docsRequeridosTitular.documentos.forEach(docRequerido => {
                const archivo = documentosTitular[docRequerido.key];
                if (!archivo || !(archivo instanceof File)) {
                    documentosFaltantes.push(`Titular - ${docRequerido.label}`);
                }
            });

            // 2. Validar documentos de cada garante
            garantes.forEach((garante, index) => {
                const docsRequeridosGarante = getDocumentosRequeridos('1', garante.tipoIngreso); // Garantes siempre son persona física
                docsRequeridosGarante.documentos.forEach(docRequerido => {
                    const archivo = documentosGarantes[index]?.[docRequerido.key];
                    if (!archivo || !(archivo instanceof File)) {
                        documentosFaltantes.push(`Garante ${index + 1} - ${docRequerido.label}`);
                    }
                });
            });

            // Si hay documentos faltantes, mostrar error y no continuar
            if (documentosFaltantes.length > 0) {
                await Swal.fire({
                    icon: 'error',
                    title: 'Documentos Faltantes',
                    html: `
                        <div class="text-left">
                            <p class="mb-3 text-gray-700">Debe cargar los siguientes documentos obligatorios:</p>
                            <ul class="list-disc list-inside space-y-1 text-sm text-gray-600">
                                ${documentosFaltantes.map(doc => `<li>${doc}</li>`).join('')}
                            </ul>
                        </div>
                    `,
                    confirmButtonColor: '#ef4444',
                });
                return;
            }
            // ========== FIN VALIDACIÓN ==========

            // Recolectar todos los archivos para subir
            const archivosParaSubir = [];
            // Documentos del titular
            Object.entries(documentosTitular).forEach(([tipo, archivo]) => {
                if (archivo && archivo instanceof File) {
                    archivosParaSubir.push({
                        file: archivo,
                        documentType: tipo,
                        personType: 'titular',
                        descripcion: `Titular - ${tipo}`
                    });
                }
            });
            // Documentos de los garantes
            documentosGarantes.forEach((docsGarante, index) => {
                Object.entries(docsGarante).forEach(([tipo, archivo]) => {
                    if (archivo && archivo instanceof File) {
                        archivosParaSubir.push({
                            file: archivo,
                            documentType: tipo,
                            personType: `garante${index + 1}`,
                            descripcion: `Garante ${index + 1} - ${tipo}`
                        });
                    }
                });
            });
            // Validar que haya al menos un archivo para subir
            if (archivosParaSubir.length === 0) {
                await Swal.fire({
                    icon: 'warning',
                    title: 'Sin Documentos',
                    text: 'No se han seleccionado documentos para cargar',
                    confirmButtonColor: '#ef4444',
                });
                return;
            }
            // Mostrar diálogo de progreso
            Swal.fire({
                title: 'Subiendo Documentos',
                html: `
                    <div class="text-center">
                        <div class="mb-4">
                            <div class="inline-flex items-center justify-center w-16 h-16 bg-honda-100 rounded-full mb-3">
                                <span class="material-icons text-honda-600 text-3xl animate-pulse">cloud_upload</span>
                            </div>
                        </div>
                        <p class="text-gray-600 mb-2">Subiendo documentos al servidor...</p>
                        <div class="text-lg font-bold text-honda-600" id="upload-progress">0 / ${archivosParaSubir.length}</div>
                    </div>
                `,
                allowOutsideClick: false,
                allowEscapeKey: false,
                showConfirmButton: false,
                didOpen: () => {
                    Swal.showLoading();
                }
            });
            // Subir archivos uno por uno
            const resultados = [];
            let subidosExitosamente = 0;
            for (let i = 0; i < archivosParaSubir.length; i++) {
                const { file, documentType, personType, descripcion } = archivosParaSubir[i];
                try {
                    const resultado = await uploadScoringDocument(
                        idScoring,
                        file,
                        documentType,
                        personType
                    );
                    resultados.push({
                        success: true,
                        descripcion,
                        resultado
                    });
                    subidosExitosamente++;
                    // Actualizar progreso
                    const progressElement = document.getElementById('upload-progress');
                    if (progressElement) {
                        progressElement.textContent = `${subidosExitosamente} / ${archivosParaSubir.length}`;
                    }
                } catch (error) {
                    console.error(`❌ Error subiendo ${descripcion}:`, error);
                    resultados.push({
                        success: false,
                        descripcion,
                        error: error.message
                    });
                }
            }
            // Cerrar diálogo de progreso
            Swal.close();
            // Verificar resultados
            const fallidos = resultados.filter(r => !r.success);
            if (fallidos.length === 0) {
                // Todos los archivos se subieron correctamente
                // 1️⃣ Guardar información de todos los garantes antes de cambiar estado
                try {
                    for (let i = 0; i < garantes.length; i++) {
                        const garante = garantes[i];
                        // Convertir fecha de nacimiento del garante al formato SQL
                        const fechaNacimientoGaranteSQL = garante.fechaNacimiento ? formatDateYYYYMMDD(garante.fechaNacimiento) : '';
                        // Convertir fecha de nacimiento del cónyuge del garante al formato SQL
                        const fechaNacimientoFianceSQL = garante.fianceBirthDate ? formatDateYYYYMMDD(garante.fianceBirthDate) : '';

                        const warrantorData = {
                            warrantorFullName: garante.nombreApellido || '',
                            warrantorDNI: garante.dni || '',
                            warrantorBirthDate: fechaNacimientoGaranteSQL,
                            warrantorGender: garante.sexo ? getGeneroTexto(garante.sexo) : '1 - Masculino',
                            warrantorMarriageStatus: garante.estadoCivil ? getEstadoCivilTexto(garante.estadoCivil) : '1 - Soltero',
                            warrantorIncomeType: garante.tipoIngreso ? getTipoIngresoTexto(garante.tipoIngreso) : '',
                            warrantorIncome: garante.ingresosNetos || '',
                            warrantorAddress: garante.calle || '',
                            warrantorAddressNumber: garante.numero || '',
                            warrantorAddressFloor: garante.altura || '',
                            warrantorAddressPostalCode: garante.codigoPostal || '',
                            warrantorAddressLocale: garante.localidad || '',
                            warrantorAddressProvince: garante.provincia || '',
                            warrantorPEP: garante.pep || '',
                            //
                            warrantorEmail: garante.email || '',
                            warrantorPhone: garante.telefono || '',
                            warrantorCountry: garante.nacionalidad || '',
                            warrantorTaxCondition: garante.condicionFiscal ? getCategoriaFiscalTexto(garante.condicionFiscal) : '1 - Consumidor Final' || '',

                            // Datos del cónyuge del garante (estructura flat)
                            fianceCompleteName: garante.fianceCompleteName || '',
                            fianceDocumentNumber: garante.fianceDocumentNumber || '',
                            fianceBirthDate: fechaNacimientoFianceSQL,
                            fianceGender: garante.fianceGender || '',
                            fianceNationality: garante.fianceNationality || '',
                            fiancePEP: garante.fiancePEP || '',
                        };
                        await saveWarrantorData(idScoring, warrantorData);
                    }
                } catch (garanteError) {
                    console.error('❌ Error al guardar garantes:', garanteError);
                    await Swal.fire({
                        icon: 'error',
                        title: 'Error al guardar garantes',
                        text: garanteError.message || 'No se pudieron guardar los garantes en la base de datos',
                        confirmButtonColor: '#ef4444',
                    });
                    return; // No continuar si falla el guardado de garantes
                }
                // 2️⃣ Ahora cambiar el estado del scoring a "EN REVISION"
                try {
                    const userID = user?.id || user?.userId || 0;

                    // Verificar si el titular es PEP o LAFT para decidir el estado
                    if(nosisPEP === 'TRUE' || nosisLAFT === 'TRUE' || formData.pep == 'SI') { await setScoringStatus(idScoring, 'EN REVISION ESPECIAL', '', userID); }
                    else { await setScoringStatus(idScoring, 'EN REVISION', '', userID); }
                    
                    await Swal.fire({
                        title: '¡Documentación Completa!',
                        html: `
                            <div class="text-center">
                                <div class="mb-4">
                                    <span class="material-icons text-green-600 text-6xl">check_circle</span>
                                </div>
                                <p class="text-gray-700 mb-2">Se han cargado exitosamente <strong>${subidosExitosamente} documentos</strong>.</p>
                                <p class="text-gray-600">El análisis crediticio está completo y ha sido enviado a revisión.</p>
                            </div>
                        `,
                        icon: 'success',
                        confirmButtonText: 'Aceptar',
                        confirmButtonColor: '#22c55e',
                        allowOutsideClick: false
                    }).then(() => {
                        // Redirigir al menú principal después de hacer clic en Aceptar
                        onNavigate('home');
                    });
                } catch (statusError) {
                    console.error('❌ Error al cambiar el estado del scoring:', statusError);
                    await Swal.fire({
                        title: 'Documentos Cargados',
                        html: `
                            <div class="text-center">
                                <div class="mb-4">
                                    <span class="material-icons text-yellow-600 text-6xl">warning</span>
                                </div>
                                <p class="text-gray-700 mb-2">Se han cargado exitosamente <strong>${subidosExitosamente} documentos</strong>.</p>
                                <p class="text-red-600 text-sm">Sin embargo, hubo un error al cambiar el estado a "EN REVISION". Por favor, contacte al administrador.</p>
                            </div>
                        `,
                        icon: 'warning',
                        confirmButtonText: 'Aceptar',
                        confirmButtonColor: '#ef4444',
                    });
                }
            } else {
                // Hubo errores en algunos archivos
                const exitosos = resultados.filter(r => r.success).length;
                await Swal.fire({
                    title: 'Carga Parcial',
                    html: `
                        <div class="text-left">
                            <p class="mb-3"><strong>Exitosos:</strong> ${exitosos} documentos</p>
                            <p class="mb-3"><strong>Fallidos:</strong> ${fallidos.length} documentos</p>
                            <div class="bg-red-50 border border-red-200 rounded p-3 mt-3">
                                <p class="font-semibold text-red-800 mb-2">Documentos con error:</p>
                                <ul class="text-sm text-red-700 list-disc list-inside">
                                    ${fallidos.map(f => `<li>${f.descripcion}: ${f.error}</li>`).join('')}
                                </ul>
                            </div>
                        </div>
                    `,
                    icon: 'warning',
                    confirmButtonText: 'Aceptar',
                    confirmButtonColor: '#ef4444',
                });
            }
        } catch (error) {
            console.error('Error en carga de documentos:', error);
            await Swal.fire({
                icon: 'error',
                title: 'Error en Carga',
                text: error.message || 'No se pudieron cargar los documentos',
                confirmButtonColor: '#ef4444',
            });
        }
    };
    return (
        <MainLayout user={user} onLogout={onLogout} onNavigate={onNavigate}>
            <div className="max-w-full mx-auto px-3 py-4 animate-fade-in-up">
                {/* Título */}
                <div className="mb-8">
                    <div className="flex items-center justify-between">
                        <div>
                            <div className="flex items-center gap-3 mb-2">
                                <div className="w-1.5 h-8 gradient-red rounded-full shadow-glow-red"></div>
                                <h2 className="text-2xl font-bold text-gradient">
                                    Análisis Crediticio
                                </h2>
                            </div>
                            <p className="text-gray-500 text-sm ml-5">Complete la información del plan y del titular para iniciar el proceso de scoring crediticio</p>
                        </div>
                        {/* Datos de Grupo y Orden centrados con el título y descripción */}
                        <div className="flex gap-4">
                            <div className="bg-gradient-to-br from-honda-50 to-white px-6 py-3 rounded-xl border border-honda-200 shadow-soft">
                                <span className="text-xs text-gray-500 uppercase tracking-wide">Grupo:</span>
                                <span className="ml-2 text-sm font-bold text-honda-700">{formData.grupo}</span>
                            </div>
                            <div className="bg-gradient-to-br from-honda-50 to-white px-6 py-3 rounded-xl border border-honda-200 shadow-soft">
                                <span className="text-xs text-gray-500 uppercase tracking-wide">Orden:</span>
                                <span className="ml-2 text-sm font-bold text-honda-700">{formData.orden}</span>
                            </div>
                        </div>
                    </div>
                </div>
                {/* SECCIÓN DATOS DEL PLAN - Solo lectura */}
                <div className="mb-6 bg-white border-2 border-gray-100 rounded-2xl shadow-soft hover:shadow-xl transition-all duration-300 overflow-hidden group">
                    <div className="h-1.5 gradient-red group-hover:h-2 transition-all duration-300"></div>
                    <div className="bg-gradient-to-br from-honda-50 to-white px-6 py-4 border-b border-gray-100">
                        <h3 className="text-sm font-bold text-honda-800 uppercase tracking-wide flex items-center gap-2">
                            <span className="material-icons text-honda-600">description</span>
                            DATOS DEL PLAN
                        </h3>
                    </div>
                    <div className="p-4">
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                            <div>
                                <label className="block text-xs font-medium text-gray-500 mb-1">Grupo</label>
                                <input
                                    type="text"
                                    name="grupo"
                                    value={formData.grupo}
                                    onChange={handleChange}
                                    readOnly
                                    className="w-full px-3 py-2 bg-gray-100 border border-gray-200 rounded-md text-gray-800 text-sm focus:outline-none cursor-default"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-500 mb-1">Orden</label>
                                <input
                                    type="text"
                                    name="orden"
                                    value={formData.orden}
                                    onChange={handleChange}
                                    readOnly
                                    className="w-full px-3 py-2 bg-gray-100 border border-gray-200 rounded-md text-gray-800 text-sm focus:outline-none cursor-default"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-500 mb-1">Tipo Persona</label>
                                <input
                                        type="text"
                                        name="tipoPersona"
                                        value={formData.tipoPersona ? getTipoPersonaTexto(formData.tipoPersona) : ''}
                                        readOnly
                                        className="w-full px-3 py-2 bg-gray-100 border border-gray-200 rounded-md text-gray-800 text-sm focus:outline-none cursor-default"
                                    />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-500 mb-1">{isPersonaJuridica ? 'Empresa' : 'Titular'}</label>
                                <input
                                    type="text"
                                    name="titular"
                                    value={formData.titular}
                                    onChange={handleChange}
                                    readOnly
                                    className="w-full px-3 py-2 bg-gray-100 border border-gray-200 rounded-md text-gray-800 text-sm focus:outline-none cursor-default"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-500 mb-1">{isPersonaJuridica ? 'CUIT' : 'DNI'}</label>
                                <input
                                    type="text"
                                    name="cuit"
                                    value={isPersonaJuridica ? formatCuit(formData.cuitEmpresa || formData.cuit) : formData.cuit}
                                    onChange={handleChange}
                                    readOnly
                                    className="w-full px-3 py-2 bg-gray-100 border border-gray-200 rounded-md text-gray-800 text-sm focus:outline-none cursor-default"
                                />
                            </div>
                            {!isPersonaJuridica && (
                            <div>
                                <label className="block text-xs font-medium text-gray-500 mb-1">Fecha Nacimiento</label>
                                <input
                                    type="text"
                                    name="fechaNacimiento"
                                    value={formatDateDDMMYYYY(formData.fechaNacimiento)}
                                    onChange={handleChange}
                                    readOnly
                                    placeholder="dd/mm/aaaa"
                                    className="w-full px-3 py-2 bg-gray-100 border border-gray-200 rounded-md text-gray-800 text-sm focus:outline-none cursor-default"
                                />
                            </div>
                            )}
                        </div>
                    </div>
                </div>
                {/* SECCIÓN STATUS DEL PLAN - Solo lectura */}
                <div className="mb-6 bg-white border-2 border-gray-100 rounded-2xl shadow-soft hover:shadow-xl transition-all duration-300 overflow-hidden group">
                    <div className="h-1.5 gradient-red group-hover:h-2 transition-all duration-300"></div>
                    <div className="bg-gradient-to-br from-honda-50 to-white px-6 py-4 border-b border-gray-100">
                        <h3 className="text-sm font-bold text-honda-800 uppercase tracking-wide flex items-center gap-2">
                            <span className="material-icons text-honda-600">assessment</span>
                            STATUS DEL PLAN
                        </h3>
                    </div>
                    <div className="p-4">
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                            <div>
                                <label className="block text-xs font-medium text-gray-500 mb-1">Modelo Suscripto</label>
                                <input
                                    type="text"
                                    name="modelo"
                                    value={formData.modelo}
                                    onChange={handleChange}
                                    readOnly
                                    className="w-full px-3 py-2 bg-gray-100 border border-gray-200 rounded-md text-gray-800 text-sm focus:outline-none cursor-default"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-500 mb-1">Fecha Adjudicación</label>
                                <input
                                    type="text"
                                    name="fechaAdjudicacion"
                                    value={formatDateDDMMYYYY(formData.fechaAdjudicacion)}
                                    onChange={handleChange}
                                    readOnly
                                    placeholder="dd/mm/aaaa"
                                    className="w-full px-3 py-2 bg-gray-100 border border-gray-200 rounded-md text-gray-800 text-sm focus:outline-none cursor-default"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-500 mb-1">Saldo a Pagar</label>
                                <input
                                    type="text"
                                    name="saldoPagar"
                                    value={formData.saldoPagar}
                                    onChange={handleChange}
                                    readOnly
                                    className="w-full px-3 py-2 bg-gray-100 border border-gray-200 rounded-md text-gray-800 text-sm focus:outline-none cursor-default"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-500 mb-1">Mesa Plan</label>
                                <input
                                    type="text"
                                    name="mesaPlan"
                                    value={formData.mesaPlan}
                                    onChange={handleChange}
                                    readOnly
                                    className="w-full px-3 py-2 bg-gray-100 border border-gray-200 rounded-md text-gray-800 text-sm focus:outline-none cursor-default"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-500 mb-1">Fecha Aceptación</label>
                                <input
                                    type="text"
                                    name="fechaAceptacion"
                                    value={formatDateDDMMYYYY(formData.fechaAceptacion)}
                                    onChange={handleChange}
                                    readOnly
                                    placeholder="dd/mm/aaaa"
                                    className="w-full px-3 py-2 bg-gray-100 border border-gray-200 rounded-md text-gray-800 text-sm focus:outline-none cursor-default"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-500 mb-1">Cuotas a Vencer</label>
                                <input
                                    type="text"
                                    name="cuotasVencer"
                                    value={formData.cuotasVencer}
                                    onChange={handleChange}
                                    readOnly
                                    className="w-full px-3 py-2 bg-gray-100 border border-gray-200 rounded-md text-gray-800 text-sm focus:outline-none cursor-default"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-500 mb-1">Valor Móvil</label>
                                <input
                                    type="text"
                                    name="valorMovil"
                                    value={formData.valorMovil}
                                    onChange={handleChange}
                                    readOnly
                                    className="w-full px-3 py-2 bg-gray-100 border border-gray-200 rounded-md text-gray-800 text-sm focus:outline-none cursor-default"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-500 mb-1">Status Stet. Central</label>
                                <input
                                    type="text"
                                    name="status"
                                    value={formData.status}
                                    onChange={handleChange}
                                    readOnly
                                    className="w-full px-3 py-2 bg-gray-100 border border-gray-200 rounded-md text-gray-800 text-sm focus:outline-none cursor-default"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-500 mb-1">Concesionario</label>
                                <input
                                    type="text"
                                    name="concesionario"
                                    value={formData.concesionario}
                                    onChange={handleChange}
                                    readOnly
                                    className="w-full px-3 py-2 bg-gray-100 border border-gray-200 rounded-md text-gray-800 text-sm focus:outline-none cursor-default"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-500 mb-1">Tipo Adjudicación</label>
                                <input
                                    type="text"
                                    name="tipoAdjudicacion"
                                    value={formData.tipoAdjudicacion}
                                    onChange={handleChange}
                                    readOnly
                                    className="w-full px-3 py-2 bg-gray-100 border border-gray-200 rounded-md text-gray-800 text-sm focus:outline-none cursor-default"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-500 mb-1">Valor Cuota</label>
                                <input
                                    type="text"
                                    name="valorCuota"
                                    value={formData.valorCuota}
                                    onChange={handleChange}
                                    readOnly
                                    className="w-full px-3 py-2 bg-gray-100 border border-gray-200 rounded-md text-gray-800 text-sm focus:outline-none cursor-default"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-500 mb-1">Porcentaje Deuda</label>
                                <input
                                    type="text"
                                    name="porcentajeDeuda"
                                    value={formData.porcentajeDeuda}
                                    onChange={handleChange}
                                    readOnly
                                    className="w-full px-3 py-2 bg-gray-100 border border-gray-200 rounded-md text-gray-800 text-sm focus:outline-none cursor-default"
                                />
                            </div>
                        </div>
                    </div>
                </div>
                {/* SECCIÓN DATOS DEL TITULAR - Solo Persona Física */}
                {!isPersonaJuridica && (
                <div className="mb-6 bg-white border-2 border-gray-100 rounded-2xl shadow-soft hover:shadow-xl transition-all duration-300 overflow-hidden group">
                    <div className="h-1.5 gradient-red group-hover:h-2 transition-all duration-300"></div>
                    <div className="bg-gradient-to-br from-honda-50 to-white px-6 py-4 border-b border-gray-100">
                        <h3 className="text-sm font-bold text-honda-800 uppercase tracking-wide flex items-center gap-2">
                            <span className="material-icons text-honda-600">person</span>
                            DATOS DEL TITULAR
                        </h3>
                    </div>
                    <div className="p-4">
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                            <div>
                                <label className="block text-xs font-medium text-gray-500 mb-1">Estado Civil <span className="text-red-500">*</span></label>
                                <select
                                    name="estadoCivil"
                                    value={formData.estadoCivil}
                                    onChange={handleChange}
                                    disabled={caseType === 'STORED'}
                                    className={`w-full px-3 py-2 border border-gray-200 rounded-md text-gray-800 text-sm focus:outline-none transition-all duration-200 ${caseType === 'STORED'
                                        ? 'bg-gray-100 cursor-default'
                                        : 'bg-gray-50 focus:border-honda-500 focus:ring-1 focus:ring-honda-500'
                                        }`}
                                >
                                    <option value="">Seleccione...</option>
                                    {ESTADO_CIVIL.map(item => (
                                        <option key={item.id} value={item.id}>
                                            {item.id} - {item.descripcion}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div>

                                <label className="block text-xs font-medium text-gray-500 mb-1">Sexo <span className="text-red-500">*</span> <span className="text-honda-700">(según se consigna en el DNI)</span></label>
                                <select
                                    name="sexo"
                                    value={formData.sexo}
                                    onChange={handleChange}
                                    disabled={caseType === 'STORED'}
                                    className={`w-full px-3 py-2 border border-gray-200 rounded-md text-gray-800 text-sm focus:outline-none transition-all duration-200 ${caseType === 'STORED'
                                        ? 'bg-gray-100 cursor-default'
                                        : 'bg-gray-50 focus:border-honda-500 focus:ring-1 focus:ring-honda-500'
                                        }`}
                                >
                                    <option value="">Seleccione...</option>
                                    {GENERO.map(item => (
                                        <option key={item.id} value={item.id}>
                                            {item.id} - {item.descripcion}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-500 mb-1">Ingresos Netos <span className="text-red-500">*</span></label>
                                <input
                                    type="text"
                                    name="ingresosNetos"
                                    value={formData.ingresosNetos}
                                    onChange={handleChange}
                                    readOnly={caseType === 'STORED'}
                                    placeholder="AR$ 0,00"
                                    className={`w-full px-3 py-2 border border-gray-200 rounded-md text-gray-800 text-sm focus:outline-none transition-all duration-200 ${caseType === 'STORED'
                                        ? 'bg-gray-100 cursor-default'
                                        : 'bg-gray-50 focus:border-honda-500 focus:ring-1 focus:ring-honda-500'
                                        }`}
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-500 mb-1">Nacionalidad <span className="text-red-500">*</span></label>
                                <input
                                    type="text"
                                    name="nacionalidad"
                                    value={formData.nacionalidad}
                                    onChange={handleChange}
                                    readOnly={caseType === 'STORED'}
                                    placeholder="Nacionalidad"
                                    className={`w-full px-3 py-2 border border-gray-200 rounded-md text-gray-800 text-sm focus:outline-none transition-all duration-200 ${caseType === 'STORED'
                                        ? 'bg-gray-100 cursor-default'
                                        : 'bg-gray-50 focus:border-honda-500 focus:ring-1 focus:ring-honda-500'
                                        }`}
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-500 mb-1">Tipo de Ingreso <span className="text-red-500">*</span></label>
                                <select
                                    name="tipoIngreso"
                                    value={formData.tipoIngreso}
                                    onChange={handleChange}
                                    disabled={caseType === 'STORED'}
                                    className={`w-full px-3 py-2 border border-gray-200 rounded-md text-gray-800 text-sm focus:outline-none transition-all duration-200 ${caseType === 'STORED'
                                        ? 'bg-gray-100 cursor-default'
                                        : 'bg-gray-50 focus:border-honda-500 focus:ring-1 focus:ring-honda-500'
                                        }`}
                                >
                                    <option value="">Seleccione...</option>
                                    {TIPO_INGRESO.map(item => (
                                        <option key={item.id} value={item.id}>
                                            {item.id} - {item.descripcion}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-500 mb-1">PEP <span className="text-red-500">*</span></label>
                                <select
                                    name="pep"
                                    value={formData.pep}
                                    onChange={handleChange}
                                    disabled={caseType === 'STORED'}
                                    className={`w-full px-3 py-2 border border-gray-200 rounded-md text-gray-800 text-sm focus:outline-none transition-all duration-200 ${caseType === 'STORED'
                                        ? 'bg-gray-100 cursor-default'
                                        : 'bg-gray-50 focus:border-honda-500 focus:ring-1 focus:ring-honda-500'
                                        }`}
                                >
                                    <option value="">Seleccione...</option>
                                    <option value="SI">Sí</option>
                                    <option value="NO">No</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-500 mb-1">Antigüedad Laboral <span className="text-red-500">*</span></label>
                                <input
                                    type="text"
                                    name="antiguedadLaboral"
                                    value={formData.antiguedadLaboral}
                                    onChange={handleChange}
                                    readOnly={caseType === 'STORED'}
                                    placeholder="Ej: 1 año, 2 años, etc."
                                    className={`w-full px-3 py-2 border border-gray-200 rounded-md text-gray-800 text-sm focus:outline-none transition-all duration-200 ${caseType === 'STORED'
                                        ? 'bg-gray-100 cursor-default'
                                        : 'bg-gray-50 focus:border-honda-500 focus:ring-1 focus:ring-honda-500'
                                        }`}
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-500 mb-1">Categoría Fiscal <span className="text-red-500">*</span></label>
                                <select
                                    name="categoriaFiscal"
                                    value={formData.categoriaFiscal}
                                    onChange={handleChange}
                                    disabled={caseType === 'STORED'}
                                    className={`w-full px-3 py-2 border border-gray-200 rounded-md text-gray-800 text-sm focus:outline-none transition-all duration-200 ${caseType === 'STORED'
                                        ? 'bg-gray-100 cursor-default'
                                        : 'bg-gray-50 focus:border-honda-500 focus:ring-1 focus:ring-honda-500'
                                        }`}
                                >
                                    <option value="">Seleccione...</option>
                                    {CATEGORIA_FISCAL.map(item => (
                                        <option key={item.id} value={item.id}>
                                            {item.id} - {item.descripcion}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>
                </div>
                )} {/* Fin !isPersonaJuridica DATOS DEL TITULAR */}
                {!isPersonaJuridica && Number(formData.estadoCivil) === 2 && (
                    <FianceCard
                        data={formData}
                        setData={setFormData}
                        prefix="fiance"
                    />
                )}
                {/* SECCIÓN DIRECCION DEL TITULAR - Solo Persona Física */}
                {!isPersonaJuridica && (
                <div className="mb-6 bg-white border-2 border-gray-100 rounded-2xl shadow-soft hover:shadow-xl transition-all duration-300 overflow-hidden group">
                    <div className="h-1.5 gradient-red group-hover:h-2 transition-all duration-300"></div>
                    <div className="bg-gradient-to-br from-honda-50 to-white px-6 py-4 border-b border-gray-100">
                        <h3 className="text-sm font-bold text-honda-800 uppercase tracking-wide flex items-center gap-2">
                            <span className="material-icons text-honda-600">location_on</span>
                            DIRECCION DEL TITULAR
                        </h3>
                    </div>
                    <div className="p-4">
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                            <div>
                                <label className="block text-xs font-medium text-gray-500 mb-1">Dirección <span className="text-red-500">*</span></label>
                                <input
                                    type="text"
                                    name="direccion"
                                    value={formData.direccion}
                                    onChange={handleChange}
                                    readOnly={caseType === 'STORED'}
                                    placeholder="Calle"
                                    className={`w-full px-3 py-2 border border-gray-200 rounded-md text-gray-800 text-sm focus:outline-none transition-all duration-200 ${caseType === 'STORED'
                                        ? 'bg-gray-100 cursor-default'
                                        : 'bg-gray-50 focus:border-honda-500 focus:ring-1 focus:ring-honda-500'
                                        }`}
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-500 mb-1">Número <span className="text-red-500">*</span></label>
                                <input
                                    type="text"
                                    name="numero"
                                    value={formData.numero}
                                    onChange={handleChange}
                                    readOnly={caseType === 'STORED'}
                                    placeholder="Número"
                                    className={`w-full px-3 py-2 border border-gray-200 rounded-md text-gray-800 text-sm focus:outline-none transition-all duration-200 ${caseType === 'STORED'
                                        ? 'bg-gray-100 cursor-default'
                                        : 'bg-gray-50 focus:border-honda-500 focus:ring-1 focus:ring-honda-500'
                                        }`}
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-500 mb-1">Piso / Dpto</label>
                                <input
                                    type="text"
                                    name="pisoDpto"
                                    value={formData.pisoDpto}
                                    onChange={handleChange}
                                    readOnly={caseType === 'STORED'}
                                    placeholder="Piso / Dpto"
                                    className={`w-full px-3 py-2 border border-gray-200 rounded-md text-gray-800 text-sm focus:outline-none transition-all duration-200 ${caseType === 'STORED'
                                        ? 'bg-gray-100 cursor-default'
                                        : 'bg-gray-50 focus:border-honda-500 focus:ring-1 focus:ring-honda-500'
                                        }`}
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-500 mb-1">Código Postal <span className="text-red-500">*</span></label>
                                <input
                                    type="text"
                                    name="codigoPostal"
                                    value={formData.codigoPostal}
                                    onChange={handleChange}
                                    readOnly={caseType === 'STORED'}
                                    placeholder="Código Postal"
                                    className={`w-full px-3 py-2 border border-gray-200 rounded-md text-gray-800 text-sm focus:outline-none transition-all duration-200 ${caseType === 'STORED'
                                        ? 'bg-gray-100 cursor-default'
                                        : 'bg-gray-50 focus:border-honda-500 focus:ring-1 focus:ring-honda-500'
                                        }`}
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-500 mb-1">Localidad <span className="text-red-500">*</span></label>
                                <input
                                    type="text"
                                    name="localidad"
                                    value={formData.localidad}
                                    onChange={handleChange}
                                    readOnly={caseType === 'STORED'}
                                    placeholder="Ciudad/Localidad"
                                    className={`w-full px-3 py-2 border border-gray-200 rounded-md text-gray-800 text-sm focus:outline-none transition-all duration-200 ${caseType === 'STORED'
                                        ? 'bg-gray-100 cursor-default'
                                        : 'bg-gray-50 focus:border-honda-500 focus:ring-1 focus:ring-honda-500'
                                        }`}
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-500 mb-1">Provincia <span className="text-red-500">*</span></label>
                                <select
                                    name="provincia"
                                    value={formData.provincia}
                                    onChange={handleChange}
                                    disabled={caseType === 'STORED'}
                                    className={`w-full px-3 py-2 border border-gray-200 rounded-md text-gray-800 text-sm focus:outline-none transition-all duration-200 ${caseType === 'STORED'
                                        ? 'bg-gray-100 cursor-default'
                                        : 'bg-gray-50 focus:border-honda-500 focus:ring-1 focus:ring-honda-500'
                                        }`}
                                >
                                    <option value="">Seleccione...</option>
                                    <option value="Buenos Aires">Buenos Aires</option>
                                    <option value="CABA">CABA</option>
                                    <option value="Catamarca">Catamarca</option>
                                    <option value="Chaco">Chaco</option>
                                    <option value="Chubut">Chubut</option>
                                    <option value="Córdoba">Córdoba</option>
                                    <option value="Corrientes">Corrientes</option>
                                    <option value="Entre Ríos">Entre Ríos</option>
                                    <option value="Formosa">Formosa</option>
                                    <option value="Jujuy">Jujuy</option>
                                    <option value="La Pampa">La Pampa</option>
                                    <option value="La Rioja">La Rioja</option>
                                    <option value="Mendoza">Mendoza</option>
                                    <option value="Misiones">Misiones</option>
                                    <option value="Neuquén">Neuquén</option>
                                    <option value="Río Negro">Río Negro</option>
                                    <option value="Salta">Salta</option>
                                    <option value="San Juan">San Juan</option>
                                    <option value="San Luis">San Luis</option>
                                    <option value="Santa Cruz">Santa Cruz</option>
                                    <option value="Santa Fe">Santa Fe</option>
                                    <option value="Santiago del Estero">Santiago del Estero</option>
                                    <option value="Tierra del Fuego">Tierra del Fuego</option>
                                    <option value="Tucumán">Tucumán</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-500 mb-1">Teléfono <span className="text-red-500">*</span></label>
                                <input
                                    type="text"
                                    name="telefono"
                                    value={formData.telefono}
                                    onChange={handleChange}
                                    readOnly={caseType === 'STORED'}
                                    placeholder="Teléfono"
                                    className={`w-full px-3 py-2 border border-gray-200 rounded-md text-gray-800 text-sm focus:outline-none transition-all duration-200 ${caseType === 'STORED'
                                        ? 'bg-gray-100 cursor-default'
                                        : 'bg-gray-50 focus:border-honda-500 focus:ring-1 focus:ring-honda-500'
                                        }`}
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-500 mb-1">E-Mail <span className="text-red-500">*</span></label>
                                <div className="flex gap-2">
                                    <input
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        readOnly={caseType === 'STORED'}
                                        placeholder="micorreo@dominio.com"
                                        className={`flex-1 px-3 py-2 border border-gray-200 rounded-md text-gray-800 text-sm focus:outline-none transition-all duration-200 ${caseType === 'STORED'
                                            ? 'bg-gray-100 cursor-default'
                                            : 'bg-gray-50 focus:border-honda-500 focus:ring-1 focus:ring-honda-500'
                                            }`}
                                    />
                                    {caseType !== 'STORED' && (
                                        <button
                                            onClick={handleValidarEmail}
                                            type="button"
                                            className="px-4 py-2 gradient-red hover:shadow-glow-red text-white rounded-lg transition-all duration-300 text-sm font-medium whitespace-nowrap hover:scale-105 transform"
                                        >
                                            Validar
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                )} {/* Fin !isPersonaJuridica DIRECCIÓN DEL TITULAR */}

                {/* SECCIÓN PERSONA JURÍDICA - Solo cuando tipoPersona es Jurídica */}
                {isPersonaJuridica && (
                    <PersonaJuridicaForm
                        formData={formData}
                        setFormData={setFormData}
                        caseType={caseType}
                    />
                )}
                {isPersonaJuridica && (
                    <ApoderadoForm
                        formData={formData}
                        setFormData={setFormData}
                        caseType={caseType}
                    />
                )}

                {/* SECCIÓN INFORMACIÓN FINANCIERA */}
                <div className="mt-6 bg-white border-2 border-gray-100 rounded-2xl shadow-soft hover:shadow-xl transition-all duration-300 overflow-hidden group">
                    <div className="h-1.5 gradient-red group-hover:h-2 transition-all duration-300"></div>
                    <div className="bg-gradient-to-br from-honda-50 to-white px-6 py-4 border-b border-gray-100">
                        <h3 className="text-sm font-bold text-honda-800 uppercase tracking-wide flex items-center gap-2">
                            <span className="material-icons text-honda-600">account_balance</span>
                            Información Financiera
                        </h3>
                    </div>
                    <div className="p-6">
                        {!showResultados ? (
                            loading ? (
                                <HondaLoader
                                    message="Validando información..."
                                    variant="inline"
                                    size="medium"
                                    subtitle="Estamos procesando el análisis crediticio del cliente"
                                />
                            ) : (
                                <div className="text-center py-8">
                                    <p className="text-gray-600 mb-6">Para continuar con el análisis, debe verificar la situación crediticia del cliente.</p>
                                    <button
                                        onClick={handleVerificarCrediticio}
                                        className="px-8 py-3 gradient-red hover:shadow-glow-red text-white rounded-xl transition-all duration-300 font-semibold shadow-soft hover:scale-105 transform"
                                    >
                                        <span className="flex items-center gap-2">
                                            <span className="material-icons">search</span>
                                            Verificar Situación Crediticia
                                        </span>
                                    </button>
                                </div>
                            )
                        ) : (
                            <div>
                                {/* Mensaje cuando el titular es APTO con o sin condiciones */}
                                {garantesRequeridos === 0 && (
                                    <div className="bg-gradient-to-br from-green-50 to-white border-2 border-green-200 rounded-2xl p-8 mb-6 shadow-soft">
                                        <div className="text-center">
                                            <div className="inline-flex items-center justify-center w-24 h-24 bg-green-100 rounded-full shadow-md mb-6">
                                                <span className="material-icons text-6xl text-green-600">check_circle</span>
                                            </div>
                                            <p className="text-gray-600 mb-3 text-base">
                                                De acuerdo al análisis crediticio realizado:
                                            </p>
                                            <p className="font-bold text-green-700 text-3xl mb-2">
                                                TITULAR APTO
                                            </p>
                                            <p className="text-gray-600 text-sm">
                                                El titular puede continuar con el proceso sin requerir garantes.
                                            </p>
                                        </div>
                                    </div>
                                )}

                                {/* Mensaje de requerimiento de garante */}
                                {garantesRequeridos > 0 && (
                                    <div className="bg-gradient-to-br from-blue-50 to-white border-2 border-blue-200 rounded-2xl p-8 mb-6 shadow-soft">
                                        <div className="text-center">
                                            <div className="inline-flex items-center justify-center gap-3 mb-6">
                                                {Array.from({ length: garantesRequeridos }).map((_, idx) => (
                                                    <div key={idx} className="inline-flex items-center justify-center w-20 h-20 bg-blue-100 rounded-full shadow-md">
                                                        <span className="material-icons text-4xl text-blue-600">person</span>
                                                    </div>
                                                ))}
                                            </div>
                                            <p className="text-gray-600 mb-3 text-base">
                                                De acuerdo a la información financiera del cliente, el sistema le pedirá a continuación:
                                            </p>
                                            <p className="font-bold text-blue-700 text-3xl mb-2">
                                                {garantesRequeridos === 1 ? 'UN (1) GARANTE' : `${garantesRequeridos === 2 ? 'DOS' : garantesRequeridos} (${garantesRequeridos}) GARANTES`}
                                            </p>
                                        </div>
                                    </div>
                                )}
                                {/* Botones de acción */}
                                {!mostrarFormularioGarante && garantes.length === 0 && (
                                    <div className="flex justify-center gap-4 mb-8">
                                        <button
                                            onClick={handleReiniciarScoring}
                                            className="px-6 py-2.5 bg-white hover:bg-gray-50 text-gray-700 rounded-xl transition-all duration-300 font-semibold border-2 border-gray-200 hover:border-gray-300 shadow-soft hover:shadow-lg hover:-translate-y-0.5 transform"
                                        >
                                            <span className="flex items-center gap-2">
                                                <span className="material-icons">restart_alt</span>
                                                Reiniciar Scoring
                                            </span>
                                        </button>
                                        {/* Solo mostrar botón de guardar si NO viene de casos */}
                                        {!fromCasos && (
                                            <button
                                                onClick={handleGuardarCaso}
                                                disabled={casoGuardado}
                                                className={`px-6 py-2.5 rounded-xl transition-all duration-300 font-semibold shadow-soft hover:shadow-lg transform ${casoGuardado
                                                    ? 'bg-green-100 text-green-700 border-2 border-green-300 cursor-not-allowed'
                                                    : 'gradient-red hover:shadow-glow-red text-white hover:scale-105'
                                                    }`}
                                            >
                                                <span className="flex items-center gap-2">
                                                    <span className="material-icons">{casoGuardado ? 'check_circle' : 'save'}</span>
                                                    {casoGuardado ? 'Caso Guardado' : 'Guardar Caso'}
                                                </span>
                                            </button>
                                        )}
                                        {/* Si viene de casos, mostrar mensaje informativo */}
                                        {fromCasos && (
                                            <div className="px-6 py-2.5 bg-blue-50 text-blue-700 rounded-xl border-2 border-blue-200 font-semibold flex items-center gap-2">
                                                <span className="material-icons">info</span>
                                                Caso ya guardado - Continuando desde casos
                                            </div>
                                        )}
                                        <button
                                            onClick={handleContinuar}
                                            disabled={!casoGuardado}
                                            className={`px-6 py-2.5 rounded-xl transition-all duration-300 font-semibold shadow-soft transform ${!casoGuardado
                                                ? 'bg-gray-200 text-gray-400 cursor-not-allowed border-2 border-gray-300'
                                                : 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white hover:shadow-lg hover:scale-105'
                                                }`}
                                        >
                                            Continuar
                                        </button>
                                    </div>
                                )}
                                {/* Lista de garantes agregados */}
                                {garantes.length > 0 && (
                                    <div className="mb-6 animate-fade-in">
                                        {garantes.map((garante, idx) => (
                                            <React.Fragment key={idx}>
                                            <div className="mb-4 bg-white border-2 border-gray-100 rounded-2xl shadow-soft hover:shadow-xl transition-all duration-300 overflow-hidden group">
                                                <div className="h-1.5 gradient-red group-hover:h-2 transition-all duration-300"></div>
                                                <div
                                                    className="bg-gradient-to-br from-honda-50 to-white px-6 py-4 border-b border-gray-100 cursor-pointer hover:from-honda-100 transition-colors duration-200"
                                                    onClick={() => toggleGaranteExpandido(idx)}
                                                >
                                                    <div className="flex items-center justify-between">
                                                        <h3 className="text-sm font-bold text-honda-800 uppercase tracking-wide flex items-center gap-2">
                                                            <span className="material-icons text-honda-600">person</span>
                                                            GARANTE {idx + 1}
                                                        </h3>
                                                        <div className="flex items-center gap-3">
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleEditarGarante(idx);
                                                                }}
                                                                className="text-honda-600 hover:text-honda-700 text-sm underline font-medium"
                                                            >
                                                                Editar
                                                            </button>
                                                            <span className="material-icons text-gray-400 transition-transform duration-300" style={{ transform: garantesExpandidos[idx] ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                                                                expand_more
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                                {/* Contenido expandible */}
                                                {garantesExpandidos[idx] && (
                                                    <div className="p-4 animate-fade-in">

                                                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 text-sm">

                                                            <div className="md:col-span-2">
                                                                <label className="block text-xs font-medium text-gray-500 mb-1">Nombre y Apellido</label>
                                                                <p className="font-medium text-gray-800">{garante.nombreApellido}</p>
                                                            </div>

                                                            <div>
                                                                <label className="block text-xs font-medium text-gray-500 mb-1">DNI</label>
                                                                <p className="font-medium text-gray-800">{garante.dni}</p>
                                                            </div>

                                                            <div>
                                                                <label className="block text-xs font-medium text-gray-500 mb-1">Estado Civil</label>
                                                                <p className="font-medium text-gray-800">
                                                                    {(() => {
                                                                        const estadoCivil = ESTADO_CIVIL.find(
                                                                            e => e.id === parseInt(garante.estadoCivil)
                                                                        );
                                                                        return estadoCivil
                                                                            ? `${estadoCivil.id} - ${estadoCivil.descripcion}`
                                                                            : garante.estadoCivil;
                                                                    })()}
                                                                </p>
                                                            </div>

                                                            <div>
                                                                <label className="block text-xs font-medium text-gray-500 mb-1">Fecha Nacimiento</label>
                                                                <p className="font-medium text-gray-800">{garante.fechaNacimiento}</p>
                                                            </div>

                                                            <div>
                                                                <label className="block text-xs font-medium text-gray-500 mb-1">Sexo</label>
                                                                <p className="font-medium text-gray-800">
                                                                    {(() => {
                                                                        const genero = GENERO.find(g => g.id === parseInt(garante.sexo));
                                                                        return genero
                                                                            ? `${genero.id} - ${genero.descripcion}`
                                                                            : garante.sexo;
                                                                    })()}
                                                                </p>
                                                            </div>

                                                            <div>
                                                                <label className="block text-xs font-medium text-gray-500 mb-1">AV / Calle</label>
                                                                <p className="font-medium text-gray-800">{garante.calle}</p>
                                                            </div>

                                                            <div>
                                                                <label className="block text-xs font-medium text-gray-500 mb-1">Número</label>
                                                                <p className="font-medium text-gray-800">{garante.numero}</p>
                                                            </div>

                                                            <div>
                                                                <label className="block text-xs font-medium text-gray-500 mb-1">Código Postal</label>
                                                                <p className="font-medium text-gray-800">{garante.codigoPostal}</p>
                                                            </div>

                                                            <div>
                                                                <label className="block text-xs font-medium text-gray-500 mb-1">Localidad</label>
                                                                <p className="font-medium text-gray-800">{garante.localidad}</p>
                                                            </div>

                                                            <div>
                                                                <label className="block text-xs font-medium text-gray-500 mb-1">Provincia</label>
                                                                <p className="font-medium text-gray-800">{garante.provincia}</p>
                                                            </div>

                                                            <div>
                                                                <label className="block text-xs font-medium text-gray-500 mb-1">Teléfono</label>
                                                                <p className="font-medium text-gray-800">{garante.telefono}</p>
                                                            </div>

                                                            <div className="md:col-span-2">
                                                                <label className="block text-xs font-medium text-gray-500 mb-1">Email</label>
                                                                <p className="font-medium text-gray-800">{garante.email}</p>
                                                            </div>

                                                            <div>
                                                                <label className="block text-xs font-medium text-gray-500 mb-1">Tipo de Ingreso</label>
                                                                <p className="font-medium text-gray-800">
                                                                    {(() => {
                                                                        const tipoIngreso = TIPO_INGRESO.find(
                                                                            t => t.id === parseInt(garante.tipoIngreso)
                                                                        );
                                                                        return tipoIngreso
                                                                            ? `${tipoIngreso.id} - ${tipoIngreso.descripcion}`
                                                                            : garante.tipoIngreso;
                                                                    })()}
                                                                </p>
                                                            </div>

                                                            <div>
                                                                <label className="block text-xs font-medium text-gray-500 mb-1">Ingresos Netos</label>
                                                                <p className="font-medium text-gray-800">{garante.ingresosNetos}</p>
                                                            </div>

                                                            <div>
                                                                <label className="block text-xs font-medium text-gray-500 mb-1">Antigüedad Laboral</label>
                                                                <p className="font-medium text-gray-800">{garante.antiguedadLaboral}</p>
                                                            </div>

                                                            <div>
                                                                <label className="block text-xs font-medium text-gray-500 mb-1">Nacionalidad</label>
                                                                <p className="font-medium text-gray-800">{garante.nacionalidad}</p>
                                                            </div>

                                                            <div>
                                                                <label className="block text-xs font-medium text-gray-500 mb-1">Condición Fiscal</label>
                                                                <p className="font-medium text-gray-800">
                                                                    {(() => {
                                                                        const condicionFiscal = CATEGORIA_FISCAL.find(
                                                                            c => c.id === parseInt(garante.condicionFiscal)
                                                                        );
                                                                        return condicionFiscal
                                                                            ? `${condicionFiscal.id} - ${condicionFiscal.descripcion}`
                                                                            : garante.condicionFiscal;
                                                                    })()}
                                                                </p>
                                                            </div>

                                                        </div>

                                                    </div>
                                                )}
                                            </div>
                                            {Number(garante?.estadoCivil) === 2 && (
                                                <div className="mb-4">
                                                    <FianceCard
                                                        data={garante}
                                                        setData={(updated) => {
                                                            setGarantes(prev => {
                                                                const copy = [...prev];
                                                                copy[idx] = updated;
                                                                return copy;
                                                            });
                                                        }}
                                                        prefix="fiance"
                                                    />
                                                </div>
                                            )}
                                            </React.Fragment>

                                        ))}


                                        {/* Botón para agregar otro garante si es necesario */}
                                        {garantes.length < garantesRequeridos && !mostrarFormularioGarante && (
                                            <button
                                                onClick={handleAgregarNuevoGarante}
                                                className="w-full py-3 mt-2 border-2 border-dashed border-gray-300 rounded-xl text-gray-600 hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 flex items-center justify-center gap-2 font-medium text-sm"
                                            >
                                                <span className="material-icons text-lg">add</span>
                                                Agregar Garante {garantes.length + 1}
                                            </button>
                                        )}
                                        {/* Botones finales cuando se completaron todos los garantes */}
                                        {garantes.length >= garantesRequeridos && !mostrarFormularioGarante && !mostrarDocumentos && (
                                            <div className="flex justify-center gap-4 mt-6 pt-6 border-t-2 border-gray-200">
                                                <button
                                                    onClick={handleReiniciarScoring}
                                                    className="px-6 py-2.5 bg-white hover:bg-gray-50 text-gray-700 rounded-xl transition-all duration-300 font-semibold border-2 border-gray-200 hover:border-gray-300 shadow-soft hover:shadow-lg hover:-translate-y-0.5 transform"
                                                >
                                                    <span className="flex items-center gap-2">
                                                        <span className="material-icons">restart_alt</span>
                                                        Reiniciar Scoring
                                                    </span>
                                                </button>
                                                <button
                                                    onClick={handleContinuarDocumentos}
                                                    className="px-6 py-2.5 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white rounded-xl transition-all duration-300 font-semibold shadow-soft hover:shadow-lg hover:scale-105 transform"
                                                >
                                                    <span className="flex items-center gap-2">
                                                        <span className="material-icons">arrow_forward</span>
                                                        Continuar
                                                    </span>
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                )}
                                {/* Formulario del garante */}
                                {mostrarFormularioGarante && (
                                    <>
                                    <div className="mt-6 bg-white border-2 border-gray-100 rounded-2xl shadow-soft hover:shadow-xl transition-all duration-300 overflow-hidden group animate-fade-in">
                                        <div className="h-1.5 gradient-red group-hover:h-2 transition-all duration-300"></div>
                                        <div className="bg-gradient-to-br from-honda-50 to-white px-6 py-4 border-b border-gray-100">
                                            <div className="flex items-center justify-between">
                                                <h3 className="text-sm font-bold text-honda-800 uppercase tracking-wide flex items-center gap-2">
                                                    <span className="material-icons text-honda-600">
                                                        {garanteEnEdicion >= 0 ? 'edit' : 'person_add'}
                                                    </span>
                                                    {garanteEnEdicion >= 0 ? `EDITANDO GARANTE ${garanteEnEdicion + 1}` : `DATOS DEL GARANTE ${garantes.length + 1}`}
                                                </h3>
                                                {garanteEnEdicion >= 0 && (
                                                    <button
                                                        onClick={() => {
                                                            // Expandir el garante que se estaba editando
                                                            setGarantesExpandidos(prev => ({ ...prev, [garanteEnEdicion]: true }));
                                                            // Resetear el formulario
                                                            setGaranteActual({
                                                                nombreApellido: '',
                                                                dni: '',
                                                                fechaNacimiento: '',
                                                                estadoCivil: '',
                                                                sexo: '',
                                                                calle: '',
                                                                numero: '',
                                                                altura: '',
                                                                codigoPostal: '',
                                                                localidad: '',
                                                                provincia: '',
                                                                telefono: '',
                                                                email: '',
                                                                tipoIngreso: '',
                                                                ingresosNetos: '',
                                                                antiguedadLaboral: '',
                                                                nacionalidad: '',
                                                                condicionFiscal: '',
                                                                pep: '',
                                                                fianceCompleteName: '',
                                                                fianceDocumentNumber: '',
                                                                fianceBirthDate: '',
                                                                fianceGender: '',
                                                                fianceNationality: '',
                                                                fiancePEP: ''
                                                            });
                                                            setGaranteValidado(false);
                                                            setResultadosGarante(null);
                                                            setGaranteEnEdicion(-1);
                                                            setMostrarFormularioGarante(true); // Mantener el formulario abierto
                                                        }}
                                                        className="text-red-600 hover:text-red-700 hover:bg-red-50 rounded-full p-1 transition-all duration-200"
                                                    >
                                                        <span className="material-icons">close</span>
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                        <div className="p-4">
                                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                                                <div className="md:col-span-2">
                                                    <label className="block text-xs font-medium text-gray-500 mb-1">Nombre y Apellido <span className="text-red-500">*</span></label>
                                                    <input
                                                        type="text"
                                                        name="nombreApellido"
                                                        value={garanteActual.nombreApellido}
                                                        onChange={handleGaranteChange}
                                                        placeholder="Nombre y Apellido"
                                                        className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-gray-800 text-sm focus:outline-none focus:border-honda-500 focus:ring-1 focus:ring-honda-500 transition-all duration-200"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-medium text-gray-500 mb-1">DNI <span className="text-red-500">*</span></label>
                                                    <input
                                                        type="text"
                                                        name="dni"
                                                        value={garanteActual.dni}
                                                        onChange={handleGaranteChange}
                                                        placeholder="DNI"
                                                        className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-gray-800 text-sm focus:outline-none focus:border-honda-500 focus:ring-1 focus:ring-honda-500 transition-all duration-200"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-medium text-gray-500 mb-1">Estado Civil <span className="text-red-500">*</span></label>
                                                    <select
                                                        name="estadoCivil"
                                                        value={garanteActual.estadoCivil}
                                                        onChange={handleGaranteChange}
                                                        className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-gray-800 text-sm focus:outline-none focus:border-honda-500 focus:ring-1 focus:ring-honda-500 transition-all duration-200"
                                                    >
                                                        <option value="">Seleccione...</option>
                                                        {ESTADO_CIVIL.map(item => (
                                                            <option key={item.id} value={item.id}>
                                                                {item.id} - {item.descripcion}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-medium text-gray-500 mb-1">Fecha Nacimiento <span className="text-red-500">*</span></label>
                                                    <input
                                                        type="text"
                                                        name="fechaNacimiento"
                                                        value={garanteActual.fechaNacimiento}
                                                        onChange={(e) => {
                                                            const formatted = formatDateInput(e.target.value);
                                                            setGaranteActual(prev => ({ ...prev, fechaNacimiento: formatted }));
                                                        }}
                                                        placeholder="dd/mm/aaaa"
                                                        maxLength="10"
                                                        className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-gray-800 text-sm focus:outline-none focus:border-honda-500 focus:ring-1 focus:ring-honda-500 transition-all duration-200"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-medium text-gray-500 mb-1">Sexo <span className="text-red-500">*</span> <span className="text-honda-700">(según se consigna en el DNI)</span></label>
                                                    <select
                                                        name="sexo"
                                                        value={garanteActual.sexo}
                                                        onChange={handleGaranteChange}
                                                        className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-gray-800 text-sm focus:outline-none focus:border-honda-500 focus:ring-1 focus:ring-honda-500 transition-all duration-200"
                                                    >
                                                        <option value="">Seleccione...</option>
                                                        {GENERO.map(item => (
                                                            <option key={item.id} value={item.id}>
                                                                {item.id} - {item.descripcion}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-medium text-gray-500 mb-1">AV / Calle <span className="text-red-500">*</span></label>
                                                    <input
                                                        type="text"
                                                        name="calle"
                                                        value={garanteActual.calle}
                                                        onChange={handleGaranteChange}
                                                        placeholder="AV / Calle"
                                                        className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-gray-800 text-sm focus:outline-none focus:border-honda-500 focus:ring-1 focus:ring-honda-500 transition-all duration-200"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-medium text-gray-500 mb-1">Número <span className="text-red-500">*</span></label>
                                                    <input
                                                        type="text"
                                                        name="numero"
                                                        value={garanteActual.numero}
                                                        onChange={handleGaranteChange}
                                                        placeholder="Número"
                                                        className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-gray-800 text-sm focus:outline-none focus:border-honda-500 focus:ring-1 focus:ring-honda-500 transition-all duration-200"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-medium text-gray-500 mb-1">Piso / Dpto</label>
                                                    <input
                                                        type="text"
                                                        name="altura"
                                                        value={garanteActual.altura}
                                                        onChange={handleGaranteChange}
                                                        placeholder="Piso / Dpto"
                                                        className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-gray-800 text-sm focus:outline-none focus:border-honda-500 focus:ring-1 focus:ring-honda-500 transition-all duration-200"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-medium text-gray-500 mb-1">Código Postal <span className="text-red-500">*</span></label>
                                                    <input
                                                        type="text"
                                                        name="codigoPostal"
                                                        value={garanteActual.codigoPostal}
                                                        onChange={handleGaranteChange}
                                                        placeholder="Código Postal"
                                                        className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-gray-800 text-sm focus:outline-none focus:border-honda-500 focus:ring-1 focus:ring-honda-500 transition-all duration-200"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-medium text-gray-500 mb-1">Localidad <span className="text-red-500">*</span></label>
                                                    <input
                                                        type="text"
                                                        name="localidad"
                                                        value={garanteActual.localidad}
                                                        onChange={handleGaranteChange}
                                                        placeholder="Ciudad/Localidad"
                                                        className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-gray-800 text-sm focus:outline-none focus:border-honda-500 focus:ring-1 focus:ring-honda-500 transition-all duration-200"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-medium text-gray-500 mb-1">Provincia <span className="text-red-500">*</span></label>
                                                    <select
                                                        name="provincia"
                                                        value={garanteActual.provincia}
                                                        onChange={handleGaranteChange}
                                                        className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-gray-800 text-sm focus:outline-none focus:border-honda-500 focus:ring-1 focus:ring-honda-500 transition-all duration-200"
                                                    >
                                                        <option value="">Seleccione...</option>
                                                        <option value="Buenos Aires">Buenos Aires</option>
                                                        <option value="CABA">CABA</option>
                                                        <option value="Catamarca">Catamarca</option>
                                                        <option value="Chaco">Chaco</option>
                                                        <option value="Chubut">Chubut</option>
                                                        <option value="Córdoba">Córdoba</option>
                                                        <option value="Corrientes">Corrientes</option>
                                                        <option value="Entre Ríos">Entre Ríos</option>
                                                        <option value="Formosa">Formosa</option>
                                                        <option value="Jujuy">Jujuy</option>
                                                        <option value="La Pampa">La Pampa</option>
                                                        <option value="La Rioja">La Rioja</option>
                                                        <option value="Mendoza">Mendoza</option>
                                                        <option value="Misiones">Misiones</option>
                                                        <option value="Neuquén">Neuquén</option>
                                                        <option value="Río Negro">Río Negro</option>
                                                        <option value="Salta">Salta</option>
                                                        <option value="San Juan">San Juan</option>
                                                        <option value="San Luis">San Luis</option>
                                                        <option value="Santa Cruz">Santa Cruz</option>
                                                        <option value="Santa Fe">Santa Fe</option>
                                                        <option value="Santiago del Estero">Santiago del Estero</option>
                                                        <option value="Tierra del Fuego">Tierra del Fuego</option>
                                                        <option value="Tucumán">Tucumán</option>
                                                    </select>
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-medium text-gray-500 mb-1">Teléfono <span className="text-red-500">*</span></label>
                                                    <input
                                                        type="text"
                                                        name="telefono"
                                                        value={garanteActual.telefono}
                                                        onChange={handleGaranteChange}
                                                        placeholder="Teléfono"
                                                        className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-gray-800 text-sm focus:outline-none focus:border-honda-500 focus:ring-1 focus:ring-honda-500 transition-all duration-200"
                                                    />
                                                </div>
                                                <div className="md:col-span-2">
                                                    <label className="block text-xs font-medium text-gray-500 mb-1">Email <span className="text-red-500">*</span></label>
                                                    <div className="flex gap-2">
                                                        <input
                                                            type="email"
                                                            name="email"
                                                            value={garanteActual.email}
                                                            onChange={handleGaranteChange}
                                                            placeholder="garante@dominio.com"
                                                            className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-gray-800 text-sm focus:outline-none focus:border-honda-500 focus:ring-1 focus:ring-honda-500 transition-all duration-200"
                                                        />
                                                        <button
                                                            onClick={handleValidarEmailGarante}
                                                            type="button"
                                                            className="px-4 py-2 gradient-red hover:shadow-glow-red text-white rounded-lg transition-all duration-300 text-sm font-medium whitespace-nowrap hover:scale-105 transform"
                                                        >
                                                            Validar
                                                        </button>
                                                    </div>
                                                </div>
                                                {/* Campos adicionales debajo de Email */}
                                                <div>
                                                    <label className="block text-xs font-medium text-gray-500 mb-1">Tipo de Ingreso <span className="text-red-500">*</span></label>
                                                    <select
                                                        name="tipoIngreso"
                                                        value={garanteActual.tipoIngreso}
                                                        onChange={handleGaranteChange}
                                                        className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-gray-800 text-sm focus:outline-none focus:border-honda-500 focus:ring-1 focus:ring-honda-500 transition-all duration-200"
                                                    >
                                                        <option value="">Seleccione...</option>
                                                        {TIPO_INGRESO.map(item => (
                                                            <option key={item.id} value={item.id}>
                                                                {item.id} - {item.descripcion}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-medium text-gray-500 mb-1">Ingresos Netos <span className="text-red-500">*</span></label>
                                                    <input
                                                        type="text"
                                                        name="ingresosNetos"
                                                        value={garanteActual.ingresosNetos}
                                                        onChange={handleGaranteChange}
                                                        placeholder="AR$ 0,00"
                                                        className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-gray-800 text-sm focus:outline-none focus:border-honda-500 focus:ring-1 focus:ring-honda-500 transition-all duration-200"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-medium text-gray-500 mb-1">Antigüedad Laboral <span className="text-red-500">*</span></label>
                                                    <select
                                                        name="antiguedadLaboral"
                                                        value={garanteActual.antiguedadLaboral}
                                                        onChange={handleGaranteChange}
                                                        className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-gray-800 text-sm focus:outline-none focus:border-honda-500 focus:ring-1 focus:ring-honda-500 transition-all duration-200"
                                                    >
                                                        <option value="">Seleccione...</option>
                                                        <option value="menos1">Menos de 1 año</option>
                                                        <option value="1a3">1 a 3 años</option>
                                                        <option value="3a5">3 a 5 años</option>
                                                        <option value="mas5">Más de 5 años</option>
                                                    </select>
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-medium text-gray-500 mb-1">Nacionalidad <span className="text-red-500">*</span></label>
                                                    <input
                                                        type="text"
                                                        name="nacionalidad"
                                                        value={garanteActual.nacionalidad}
                                                        onChange={handleGaranteChange}
                                                        placeholder="Nacionalidad"
                                                        className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-gray-800 text-sm focus:outline-none focus:border-honda-500 focus:ring-1 focus:ring-honda-500 transition-all duration-200"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-medium text-gray-500 mb-1">Condición Fiscal <span className="text-red-500">*</span></label>
                                                    <select
                                                        name="condicionFiscal"
                                                        value={garanteActual.condicionFiscal}
                                                        onChange={handleGaranteChange}
                                                        className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-gray-800 text-sm focus:outline-none focus:border-honda-500 focus:ring-1 focus:ring-honda-500 transition-all duration-200"
                                                    >
                                                        <option value="">Seleccione...</option>
                                                        {CATEGORIA_FISCAL.map(item => (
                                                            <option key={item.id} value={item.id}>
                                                                {item.id} - {item.descripcion}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>

                                            </div>
                                        </div>
                                    </div>
                                    {garanteActual.estadoCivil === '2' && (
                                        <div className="mt-6">
                                            <FianceCard
                                                data={garanteActual}
                                                setData={setGaranteActual}
                                                prefix="fiance"
                                            />
                                        </div>
                                    )}
                                    {/* Sección de validación Veraz para el garante */}
                                    <div className="mt-6 bg-gradient-to-br from-gray-50 to-white rounded-2xl p-6">
                                                    {!garanteValidado ? (
                                                        loadingGarante ? (
                                                            <HondaLoader
                                                                message="Validando Informacion..."
                                                                variant="inline"
                                                                size="medium"
                                                                subtitle="Verificando la situación crediticia del garante"
                                                            />
                                                        ) : (
                                                            <div className="text-center py-4">
                                                                <p className="text-gray-600 mb-4 text-sm">Valide la situación crediticia del garante en Veraz</p>
                                                                <button
                                                                    onClick={handleValidarGaranteVeraz}
                                                                    className="px-6 py-2.5 gradient-red hover:shadow-glow-red text-white rounded-xl transition-all duration-300 font-semibold shadow-soft hover:scale-105 transform"
                                                                >
                                                                    <span className="flex items-center gap-2">
                                                                        <span className="material-icons text-lg">verified_user</span>
                                                                        Validar Garante
                                                                    </span>
                                                                </button>
                                                            </div>
                                                        )
                                                    ) : (
                                                        <div className="animate-fade-in">
                                                            {/* Botones de acción después de validar el garante */}
                                                            <div className="flex justify-center gap-4 mt-6 pt-6 border-t border-gray-200">
                                                                {garanteEnEdicion < 0 && (
                                                                    <button
                                                                        onClick={handleReiniciarScoring}
                                                                        className="px-6 py-2.5 bg-white hover:bg-gray-50 text-gray-700 rounded-xl transition-all duration-300 font-semibold border-2 border-gray-200 hover:border-gray-300 shadow-soft hover:shadow-lg hover:-translate-y-0.5 transform"
                                                                    >
                                                                        <span className="flex items-center gap-2">
                                                                            <span className="material-icons">restart_alt</span>
                                                                            Reiniciar Scoring
                                                                        </span>
                                                                    </button>
                                                                )}
                                                                <button
                                                                    onClick={handleAgregarGarante}
                                                                    className="px-6 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-xl transition-all duration-300 font-semibold shadow-soft hover:shadow-lg hover:scale-105 transform"
                                                                >
                                                                    <span className="flex items-center gap-2">
                                                                        <span className="material-icons">
                                                                            {garanteEnEdicion >= 0
                                                                                ? 'save'
                                                                                : (garantes.length + 1 >= garantesRequeridos ? 'check_circle' : 'arrow_forward')
                                                                            }
                                                                        </span>
                                                                        {garanteEnEdicion >= 0
                                                                            ? 'Actualizar Garante'
                                                                            : (garantes.length + 1 >= garantesRequeridos ? 'Finalizar' : 'Continuar')
                                                                        }
                                                                    </span>
                                                                </button>
                                                            </div>
                                                        </div>
                                                    )}
                                    </div>
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                </div>
                {/* SECCIÓN CARGA DE DOCUMENTACIÓN */}
                {mostrarDocumentos && (
                    <div className="mt-6 bg-white border-2 border-gray-100 rounded-2xl shadow-soft hover:shadow-xl transition-all duration-300 overflow-hidden group animate-fade-in">
                        <div className="h-1.5 gradient-red group-hover:h-2 transition-all duration-300"></div>
                        <div className="bg-gradient-to-br from-honda-50 to-white px-6 py-4 border-b border-gray-100">
                            <h3 className="text-sm font-bold text-honda-800 uppercase tracking-wide flex items-center gap-2">
                                <span className="material-icons text-honda-600">upload_file</span>
                                DOCUMENTACIÓN RESPALDATORIA
                            </h3>
                        </div>
                        <div className="p-6">
                            {/* Documentación del Titular */}
                            <div className="mb-8">
                                <div className="bg-gradient-to-br from-gray-50 to-white border-2 border-gray-100 rounded-xl p-5 mb-4">
                                    <h4 className="text-sm font-bold text-gray-700 uppercase tracking-wide mb-4 flex items-center gap-2">
                                        <span className="material-icons text-honda-600">person</span>
                                        DOCUMENTACIÓN DEL TITULAR
                                    </h4>
                                    {(() => {
                                        const docsRequeridos = getDocumentosRequeridos(formData.tipoPersona, formData.tipoIngreso);
                                        return (
                                            <div className="space-y-4">
                                                {docsRequeridos.documentos.map((doc) => (
                                                    <div key={doc.key}>
                                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                                            {doc.label}
                                                        </label>
                                                        <div className="relative">
                                                            <input
                                                                type="file"
                                                                id={doc.key}
                                                                className="hidden"
                                                                onChange={(e) => {
                                                                    if (e.target.files && e.target.files[0]) {
                                                                        handleFileChange(doc.key, e.target.files[0]);
                                                                    }
                                                                }}
                                                                accept=".pdf"
                                                            />
                                                            <label
                                                                htmlFor={doc.key}
                                                                className={`flex items-center justify-between w-full px-4 py-3 border-2 border-dashed rounded-lg cursor-pointer transition-all duration-200 ${documentosTitular[doc.key]
                                                                    ? 'bg-green-50 border-green-300 hover:bg-green-100'
                                                                    : 'bg-gray-50 border-gray-300 hover:bg-gray-100 hover:border-honda-400'
                                                                    }`}
                                                            >
                                                                <div className="flex items-center gap-2 flex-1 min-w-0">
                                                                    <span className={`material-icons text-xl ${documentosTitular[doc.key] ? 'text-green-600' : 'text-gray-400'
                                                                        }`}>
                                                                        {documentosTitular[doc.key] ? 'check_circle' : 'cloud_upload'}
                                                                    </span>
                                                                    <span className="text-sm text-gray-600 truncate">
                                                                        {documentosTitular[doc.key] ? documentosTitular[doc.key].name : 'No file selected.'}
                                                                    </span>
                                                                </div>
                                                                {documentosTitular[doc.key] && (
                                                                    <button
                                                                        type="button"
                                                                        onClick={(e) => {
                                                                            e.preventDefault();
                                                                            e.stopPropagation();
                                                                            handleFileChange(doc.key, null);
                                                                        }}
                                                                        className="ml-2 text-red-500 hover:text-red-700 transition-colors"
                                                                    >
                                                                        <span className="material-icons text-sm">close</span>
                                                                    </button>
                                                                )}
                                                            </label>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        );
                                    })()}
                                </div>
                            </div>
                            {/* Documentación de los Garantes */}
                            {garantes.length > 0 && (
                                <div className="mb-8">
                                    <h4 className="text-sm font-bold text-gray-700 uppercase tracking-wide mb-4 flex items-center gap-2">
                                        <span className="material-icons text-honda-600">people</span>
                                        DOCUMENTACIÓN DE GARANTES
                                    </h4>
                                    <div className="space-y-4">
                                        {garantes.map((garante, garanteIdx) => {
                                            const docsRequeridos = getDocumentosRequeridos('1', garante.tipoIngreso);
                                            const tipoIngresoText = (() => {
                                                const tipo = TIPO_INGRESO.find(t => t.id === parseInt(garante.tipoIngreso));
                                                if (tipo) return tipo.descripcion.toUpperCase();
                                                // Compatibilidad con valores antiguos
                                                if (garante.tipoIngreso === 'relacionDependencia') return 'EMPLEADO';
                                                if (garante.tipoIngreso === 'monotributo') return 'MONOTRIBUTISTA';
                                                if (garante.tipoIngreso === 'autonomo') return 'AUTÓNOMO';
                                                return '';
                                            })();
                                            return (
                                                <div key={garanteIdx} className="bg-gradient-to-br from-gray-50 to-white border-2 border-gray-100 rounded-xl p-5">
                                                    <h5 className="text-sm font-semibold text-honda-700 mb-4 flex items-center gap-2">
                                                        <span className="material-icons text-honda-600 text-base">person</span>
                                                        GARANTE {garanteIdx + 1} - {garante.nombreApellido}
                                                        {tipoIngresoText && ` (${tipoIngresoText})`}
                                                    </h5>
                                                    <div className="space-y-4">
                                                        {docsRequeridos.documentos.map((doc) => (
                                                            <div key={doc.key}>
                                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                                    {doc.label.replace('Titular', 'Garante')}
                                                                </label>
                                                                <div className="relative">
                                                                    <input
                                                                        type="file"
                                                                        id={`garante-${garanteIdx}-${doc.key}`}
                                                                        className="hidden"
                                                                        onChange={(e) => {
                                                                            if (e.target.files && e.target.files[0]) {
                                                                                handleFileChange(doc.key, e.target.files[0], garanteIdx);
                                                                            }
                                                                        }}
                                                                        accept=".pdf"
                                                                    />
                                                                    <label
                                                                        htmlFor={`garante-${garanteIdx}-${doc.key}`}
                                                                        className={`flex items-center justify-between w-full px-4 py-3 border-2 border-dashed rounded-lg cursor-pointer transition-all duration-200 ${documentosGarantes[garanteIdx]?.[doc.key]
                                                                            ? 'bg-green-50 border-green-300 hover:bg-green-100'
                                                                            : 'bg-gray-50 border-gray-300 hover:bg-gray-100 hover:border-honda-400'
                                                                            }`}
                                                                    >
                                                                        <div className="flex items-center gap-2 flex-1 min-w-0">
                                                                            <span className={`material-icons text-xl ${documentosGarantes[garanteIdx]?.[doc.key] ? 'text-green-600' : 'text-gray-400'
                                                                                }`}>
                                                                                {documentosGarantes[garanteIdx]?.[doc.key] ? 'check_circle' : 'cloud_upload'}
                                                                            </span>
                                                                            <span className="text-sm text-gray-600 truncate">
                                                                                {documentosGarantes[garanteIdx]?.[doc.key]
                                                                                    ? documentosGarantes[garanteIdx][doc.key].name
                                                                                    : 'No file selected.'}
                                                                            </span>
                                                                        </div>
                                                                        {documentosGarantes[garanteIdx]?.[doc.key] && (
                                                                            <button
                                                                                type="button"
                                                                                onClick={(e) => {
                                                                                    e.preventDefault();
                                                                                    e.stopPropagation();
                                                                                    handleFileChange(doc.key, null, garanteIdx);
                                                                                }}
                                                                                className="ml-2 text-red-500 hover:text-red-700 transition-colors"
                                                                            >
                                                                                <span className="material-icons text-sm">close</span>
                                                                            </button>
                                                                        )}
                                                                    </label>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                            {/* Botones finales de documentación */}
                            <div className="flex justify-center gap-4 mt-8 pt-6 border-t-2 border-gray-200">
                                <button
                                    onClick={handleReiniciarScoring}
                                    className="px-6 py-2.5 bg-white hover:bg-gray-50 text-gray-700 rounded-xl transition-all duration-300 font-semibold border-2 border-gray-200 hover:border-gray-300 shadow-soft hover:shadow-lg hover:-translate-y-0.5 transform"
                                >
                                    <span className="flex items-center gap-2">
                                        <span className="material-icons">restart_alt</span>
                                        Reiniciar Scoring
                                    </span>
                                </button>
                                <button
                                    onClick={handleFinalizarCargaDocumentos}
                                    className="px-6 py-2.5 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white rounded-xl transition-all duration-300 font-semibold shadow-soft hover:shadow-lg hover:scale-105 transform"
                                >
                                    <span className="flex items-center gap-2">
                                        <span className="material-icons">check_circle</span>
                                        Finalizar Carga de Documentos
                                    </span>
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </MainLayout>
    );
};
export default AnalisisCrediticio;

